import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { AllegatoImg, openAllegato } from "@/lib/allegati";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Plus, Trash2, ShieldCheck, Package, Users2, Euro, Calendar as CalIcon, Camera, Pencil, X, Check, Tags } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import RigaAnalisiPanel from "@/pages/RigaAnalisiPanel";
import { ExcelVoceTabs } from "@/components/excelVoce/ExcelVoceTabs";

const STATI_ORD = ["da_ordinare","ordinato","confermato","in_consegna","consegnato","in_ritardo","annullato"];

export default function RigaDetail({ rigaIdProp, cantiereIdProp, groupIdProp, inline = false, onCategoriaChanged = null } = {}) {
  const params = useParams();
  const groupId = groupIdProp || params.groupId;
  const cantiereId = cantiereIdProp || params.id;
  const rigaId = rigaIdProp || params.rigaId;
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dettaglio";
  const highlightOrdine = searchParams.get("highlight");
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user && (user.role === "ADMIN" || user.role === "TECNICO");
  const [riga, setRiga] = useState(null);
  const [dettagli, setDettagli] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [manEff, setManEff] = useState([]);
  const [foto, setFoto] = useState([]);
  const [rilieviSummary, setRilieviSummary] = useState(null);
  const [prezziContratt, setPrezziContratt] = useState([]);  // [{subappaltatore_id, prezzo_unitario}]
  const [categorie, setCategorie] = useState([]);
  const [catSaving, setCatSaving] = useState(false);

  const load = async () => {
    let data;
    try {
      ({ data } = await api.get(`/righe/${rigaId}`));
      setRiga(data);
    } catch { toast.error("Riga non trovata"); navigate(-1); return; }
    // E6.1 — le voci Excel usano il proprio dettaglio: nessuna richiesta legacy
    if (data.computo_source_type === "xlsx_analisi") return;
    // Fetch ausiliari: OGNI fallimento è isolato (una GET rotta NON deve azzerare le altre sezioni)
    const [d, o, m, f, r, pc] = await Promise.all([
      api.get(`/righe/${rigaId}/dettagli`).catch(() => ({ data: [] })),
      api.get(`/cantieri/${cantiereId}/ordini`).catch(() => ({ data: [] })),
      api.get(`/cantieri/${cantiereId}/manodopera-effettiva`).catch(() => ({ data: null })),
      api.get(`/allegati?riga_computo_id=${rigaId}&tipo=foto`).catch(() => ({ data: [] })),
      api.get(`/righe/${rigaId}/rilievi`).catch(() => ({ data: null })),
      api.get(`/righe/${rigaId}/prezzi-contrattuali`).catch(() => ({ data: [] })),
    ]);
    setDettagli(d.data || []);
    setOrdini((o.data || []).filter(x => x.riga_computo_id === rigaId));
    if (m.data !== null) setManEff((m.data || []).filter(x => x.riga_computo_id === rigaId));
    setFoto(f.data || []);
    if (r.data !== null) setRilieviSummary(r.data);
    setPrezziContratt(pc.data || []);
  };
  useEffect(() => { load(); }, [rigaId]);
  useEffect(() => {
    // Categorie operative attive (per select nel dettaglio)
    api.get(`/categorie-operative`)
      .then(({ data }) => setCategorie(data || []))
      .catch((e) => console.warn("categorie-operative load:", e?.message));
  }, []);
  const assignCategoria = async (val) => {
    if (!riga) return;
    setCatSaving(true);
    const nuovaCatId = val === "" ? null : val;
    const oldCatId = riga.categoria_operativa_id || null;
    try {
      await api.patch(`/computi/${riga.computo_id}/righe/${riga.id}/categoria`, { categoria_operativa_id: nuovaCatId });
      // aggiorna localmente senza reload completo
      setRiga(prev => prev ? { ...prev, categoria_operativa_id: nuovaCatId, categoria_operativa_manuale: nuovaCatId !== null } : prev);
      toast.success(nuovaCatId ? "Categoria assegnata" : "Categoria rimossa (torna candidabile al classificatore)");
      // Notifica il parent (ComputoTab) per spostamento visuale live in vista Categorie
      if (typeof onCategoriaChanged === "function" && oldCatId !== nuovaCatId) {
        try { onCategoriaChanged({ rigaId: riga.id, oldCatId, newCatId: nuovaCatId, manuale: nuovaCatId !== null }); } catch { /* no-op */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore assegnazione categoria");
    } finally { setCatSaving(false); }
  };
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (!riga) return <div className="text-slate-500 p-4">Caricamento…</div>;
  const tot_mat_prev = (riga.materiali_previsti || []).reduce((s, m) => s + Number(m.costo_previsto || 0), 0);
  const tot_man_prev = (riga.manodopera_prevista || []).reduce((s, m) => s + Number(m.costo_previsto || 0), 0);
  const tot_ord_mat = ordini.filter(o => o.stato !== "annullato").reduce((s, o) => s + Number(o.costo_totale || 0), 0);
  const tot_man_eff_ore = manEff.reduce((s, m) => s + Number(m.costo_totale || 0), 0);
  // Fonte prezzo scenario C: PrezzoContrattualeSub via nuovo endpoint /righe/{id}/prezzi-contrattuali.
  // Fallback su riga.manodopera_prevista solo se il nuovo endpoint non ha risposto (retro-compat).
  const manpQtaList = (riga.manodopera_prevista || []).filter(m => m.mode === "quantita");
  const prezzoBySub = (sub_id) => {
    if (!sub_id) return null;
    const fromEndpoint = prezziContratt.find(p => p.subappaltatore_id === sub_id);
    if (fromEndpoint && fromEndpoint.prezzo_unitario != null) return Number(fromEndpoint.prezzo_unitario);
    const legacy = manpQtaList.find(m => m.subappaltatore_id === sub_id)?.prezzo_contrattuale;
    return legacy != null ? Number(legacy) : null;
  };
  const rilieviList = rilieviSummary?.rilievi || [];
  const tot_man_eff_qta = rilieviList.reduce((s, r) => {
    const prezzo = prezzoBySub(r.subappaltatore_id);
    return s + (prezzo != null ? Number(r.quantita_rilevata) * prezzo : 0);
  }, 0);
  const ricavo = Number(riga.importo_totale || 0);
  const tot_prev = tot_mat_prev + tot_man_prev;
  const tot_eff_ore = tot_ord_mat + tot_man_eff_ore;
  const tot_eff_qta = tot_ord_mat + tot_man_eff_qta;
  const margine_prev = ricavo - tot_prev;
  const margine_eff_ore = ricavo - tot_eff_ore;
  const margine_eff_qta = ricavo - tot_eff_qta;
  const pct = (v) => ricavo ? (v / ricavo * 100) : 0;
  const isExcel = riga.computo_source_type === "xlsx_analisi";

  return (
    <div className={inline ? "w-full" : "max-w-7xl mx-auto"}>
      {!inline && <Button variant="ghost" size="sm" onClick={() => navigate(`/g/${groupId}/cantieri/${cantiereId}?tab=computo`)} className="mb-4"><ChevronLeft className="h-4 w-4 mr-1" /> Torna al cantiere</Button>}
      {!inline && (
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge variant="outline">{riga.computo_nome}</Badge>
          <Badge variant="outline">{riga.capitolo_nome}</Badge>
          {riga.num_ord && <Badge variant="outline" className="font-mono">N.Ord {riga.num_ord}</Badge>}
          {riga.tariffa && <Badge variant="outline" className="font-mono">{riga.tariffa}</Badge>}
          <Badge variant="outline" data-testid="riga-origin-badge">{isExcel ? "Excel con analisi" : riga.origin === "xlsx" ? "Da XLSX" : "Manuale"}</Badge>
        </div>
        <h1 className={`text-2xl font-bold ${highlightOrdine === "riga" ? "animate-pulse-highlight" : ""}`} data-testid="riga-title">{riga.designazione}</h1>
        {rilieviSummary && rilieviSummary.cumulativo > rilieviSummary.quantita_computo && rilieviSummary.quantita_computo > 0 && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-bold uppercase tracking-widest shadow-md" data-testid="riga-header-sforamento">
            <span>⚠ SFORAMENTO +{fmt(rilieviSummary.cumulativo - rilieviSummary.quantita_computo)} {rilieviSummary.unita_misura || ""}</span>
            <span className="font-mono opacity-90">· {fmt(rilieviSummary.percentuale)}%</span>
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
          <span>UM: <b className="text-slate-800 dark:text-slate-200">{riga.unita_misura || "—"}</b></span>
          <span>Quantità: <b className="text-slate-800 dark:text-slate-200 font-mono">{fmt(riga.quantita)}</b></span>
          <span>Prezzo unit.: <b className="text-slate-800 dark:text-slate-200 font-mono">€ {fmt(riga.prezzo_unitario)}</b></span>
          <span>Importo: <b className="text-amber-600 font-mono">€ {fmt(riga.importo_totale)}</b></span>
        </div>
      </div>
      )}

      {isExcel ? (
        <ExcelVoceTabs rigaId={riga.id}
          foto={<FotoPerRiga riga={riga} foto={foto} onChange={load} canEdit={canEdit} />}
          note={<NotePerRiga riga={riga} canEdit={canEdit} />}
          onDescrizioneChange={(t) => setRiga((p) => ({ ...p, designazione: t }))} />
      ) : (
      <Tabs defaultValue={initialTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="dettaglio" data-testid="riga-tab-dettaglio">Dettaglio</TabsTrigger>
          <TabsTrigger value="materiali" data-testid="riga-tab-materiali">Materiali & Ordini</TabsTrigger>
          <TabsTrigger value="manodopera" data-testid="riga-tab-manodopera">Manodopera</TabsTrigger>
          <TabsTrigger value="rilievi" data-testid="riga-tab-rilievi">Rilievi</TabsTrigger>
          <TabsTrigger value="costi" data-testid="riga-tab-costi">Controllo costi</TabsTrigger>
          <TabsTrigger value="consegne" data-testid="riga-tab-consegne">Consegne</TabsTrigger>
          <TabsTrigger value="foto" data-testid="riga-tab-foto">Foto</TabsTrigger>
          <TabsTrigger value="note" data-testid="riga-tab-note">Note</TabsTrigger>
          <TabsTrigger value="analisi" data-testid="riga-tab-analisi">Analisi Prezzo</TabsTrigger>
        </TabsList>

        <TabsContent value="dettaglio">
          <Card className="mb-4"><CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Tags className="h-4 w-4 text-slate-500" />
              <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Categoria lavorazione</div>
              <select
                value={riga.categoria_operativa_id || ""}
                onChange={(e) => assignCategoria(e.target.value)}
                disabled={!canEdit || catSaving}
                className="h-8 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 min-w-[220px]"
                data-testid="riga-categoria-select"
              >
                <option value="">— Nessuna —</option>
                {categorie.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
              {riga.categoria_operativa_manuale && (
                <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 border border-slate-300 dark:border-slate-700 rounded px-1.5 py-0.5" data-testid="riga-categoria-manuale-badge">Manuale</span>
              )}
              <span className="text-[11px] text-slate-500 italic">L'assegnazione manuale protegge la riga dal classificatore automatico. "Nessuna" resetta la protezione.</span>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Misurazioni originali (immutabili)</div>
            {dettagli.length === 0 ? <p className="text-slate-500 text-sm">Nessun dettaglio di misurazione registrato.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Descrizione</TableHead><TableHead className="text-right">Par.Ug.</TableHead><TableHead className="text-right">Lung.</TableHead><TableHead className="text-right">Larg.</TableHead><TableHead className="text-right">H/Peso</TableHead><TableHead className="text-right">Q.tà</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
                <TableBody>{dettagli.map(d => (
                  <TableRow key={d.id} data-testid={`dettaglio-row-${d.id}`}>
                    <TableCell className="text-sm">{d.designazione}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(d.par_ug)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(d.lunghezza)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(d.larghezza)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(d.h_peso)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(d.quantita)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(d.importo_totale)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
            {riga.imported_data && <div className="mt-4"><div className="text-xs text-slate-500 mb-1">Snapshot immutabile riga madre</div><pre className="text-xs bg-slate-50 dark:bg-slate-800 p-2 rounded max-h-40 overflow-auto">{JSON.stringify(riga.imported_data, null, 2)}</pre></div>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="materiali"><MaterialiOrdiniForRiga riga={riga} ordini={ordini} onChange={load} canEdit={canEdit} highlightOrdine={highlightOrdine} /></TabsContent>
        <TabsContent value="manodopera"><ManodoperaForRiga riga={riga} manEff={manEff} rilievi={rilieviSummary?.rilievi || []} onChange={load} canEdit={canEdit} /></TabsContent>
        <TabsContent value="rilievi"><RilieviPerRiga riga={riga} canEdit={canEdit} /></TabsContent>
        <TabsContent value="costi">
          <Card><CardContent className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatBox label="Importo voce computo" v={ricavo} highlight="amber" />
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">A) Costi previsti</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Materiali previsti" v={tot_mat_prev} />
                <StatBox label="Manodopera prevista" v={tot_man_prev} />
                <StatBox label="Totale previsto" v={tot_prev} />
                <StatBox label="Margine previsto" v={margine_prev} sub={`${pct(margine_prev).toFixed(1)}%`} highlight={margine_prev >= 0 ? "emerald" : "rose"} testid="cc-margine-prev" />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-2">B) Costi effettivi — Manodopera a ORE</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Ordini materiali" v={tot_ord_mat} />
                <StatBox label="Manodopera a ore" v={tot_man_eff_ore} />
                <StatBox label="Totale effettivo ore" v={tot_eff_ore} />
                <StatBox label="Margine effettivo ore" v={margine_eff_ore} sub={`${pct(margine_eff_ore).toFixed(1)}%`} highlight={margine_eff_ore >= 0 ? "emerald" : "rose"} testid="cc-margine-eff-ore" />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-2">C) Costi effettivi — Manodopera a QUANTITÀ</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Ordini materiali" v={tot_ord_mat} />
                <StatBox label="Manodopera a quantità" v={tot_man_eff_qta} />
                <StatBox label="Totale eff. quantità" v={tot_eff_qta} />
                <StatBox label="Margine eff. quantità" v={margine_eff_qta} sub={`${pct(margine_eff_qta).toFixed(1)}%`} highlight={margine_eff_qta >= 0 ? "emerald" : "rose"} testid="cc-margine-eff-qta" />
              </div>
            </div>
            <div className="text-[10px] text-slate-500 italic">MdO a quantità = Σ(quantità rilevata × prezzo contrattuale subappaltatore). Ordini materiali = OrdiniMateriale collegati alla riga (non annullati).</div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="consegne">
          <Card><CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Consegne di questa voce</div>
            {ordini.filter(o => o.data_prevista_consegna).length === 0 ? <p className="text-slate-500 text-sm">Nessuna consegna programmata per questa voce.</p> : (
              <Table><TableHeader><TableRow><TableHead>Data prevista</TableHead><TableHead>Materiale</TableHead><TableHead>Q.tà</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                <TableBody>{ordini.filter(o => o.data_prevista_consegna).sort((a,b) => a.data_prevista_consegna.localeCompare(b.data_prevista_consegna)).map(o => (
                  <TableRow key={o.id}><TableCell className="font-mono text-xs">{o.data_prevista_consegna}</TableCell><TableCell>{o.descrizione_materiale}</TableCell><TableCell className="font-mono">{fmt(o.quantita)} {o.unita_misura || ""}</TableCell><TableCell><Badge variant="outline" className="capitalize">{String(o.stato).replace("_"," ")}</Badge></TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="foto"><FotoPerRiga riga={riga} foto={foto} onChange={load} canEdit={canEdit} /></TabsContent>
        <TabsContent value="note"><NotePerRiga riga={riga} canEdit={canEdit} /></TabsContent>
        <TabsContent value="analisi"><RigaAnalisiPanel riga={riga} canEdit={canEdit} /></TabsContent>
      </Tabs>
      )}
    </div>
  );
}

function StatBox({ label, v, sub, highlight, testid }) {
  const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const HIGHLIGHT_CLS = { emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600" };
  const cls = HIGHLIGHT_CLS[highlight] || "";
  return <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4" data-testid={testid}><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className={`text-xl font-extrabold mt-1 font-mono ${cls}`}>€ {fmt(v)}</div>{sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}</div>;
}


// FASE 2C — Mini-card riepilogo materiali della voce (solo materiali generati
// da Analisi Prezzi). NON tocca materiali manuali, non mostra valori inventati.
function MaterialiVoceRecap({ riga }) {
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const generati = (riga.materiali_previsti || []).filter(m => !!m.source_analisi_risorsa_id);
  if (generati.length === 0) return null;

  const prezzo_riga = Number(riga.prezzo_unitario || 0);
  const qta_riga = Number(riga.quantita || 0);

  // Costi per UM (usa coefficiente × prezzo)
  let costoAnalisiPerUM = 0;
  let costoInviluppoPerUM = 0;
  let costoAnalisiTot = 0;
  let costoInviluppoTot = 0;
  for (const m of generati) {
    const c = Number(m.coefficiente_per_unita || 0);
    const q = Number(m.quantita_complessiva || 0);
    const pOrig = m.prezzo_unitario_originale != null ? Number(m.prezzo_unitario_originale) : null;
    const pInv = m.prezzo_unitario != null ? Number(m.prezzo_unitario) : null;
    if (pOrig != null) {
      costoAnalisiPerUM += c * pOrig;
      costoAnalisiTot += (m.costo_previsto_originale != null ? Number(m.costo_previsto_originale) : q * pOrig);
    }
    if (pInv != null) {
      costoInviluppoPerUM += c * pInv;
      costoInviluppoTot += (m.costo_previsto != null ? Number(m.costo_previsto) : q * pInv);
    }
  }
  const incAnalisi = prezzo_riga > 0 ? (costoAnalisiPerUM / prezzo_riga * 100) : null;
  const incInviluppo = prezzo_riga > 0 ? (costoInviluppoPerUM / prezzo_riga * 100) : null;
  const variazionePerUM = costoInviluppoPerUM - costoAnalisiPerUM;
  const variazionePct = costoAnalisiPerUM > 0 ? (variazionePerUM / costoAnalisiPerUM * 100) : null;

  return (
    <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/20 p-3" data-testid="materiali-voce-recap">
      <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold mb-2">Materiali della voce · Analisi vs INVILUPPO</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        <div>
          <div className="text-slate-500 text-[10px]">Prezzo lavorazione</div>
          <div className="font-semibold">€ {fmt(prezzo_riga)} /UM</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px]">Materiali Analisi</div>
          <div className="font-semibold" data-testid="recap-analisi-per-um">€ {fmt(costoAnalisiPerUM)} /UM {incAnalisi != null && <span className="text-slate-500">({fmt(incAnalisi)}%)</span>}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px]">Materiali INVILUPPO</div>
          <div className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="recap-inviluppo-per-um">€ {fmt(costoInviluppoPerUM)} /UM {incInviluppo != null && <span className="text-slate-500">({fmt(incInviluppo)}%)</span>}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px]">Variazione</div>
          <div className={`font-semibold ${variazionePerUM > 0 ? "text-rose-600" : variazionePerUM < 0 ? "text-emerald-600" : ""}`} data-testid="recap-variazione">
            {variazionePerUM > 0 ? "+" : ""}€ {fmt(variazionePerUM)} /UM
            {variazionePct != null && <span className="text-slate-500"> ({variazionePct > 0 ? "+" : ""}{fmt(variazionePct)}%)</span>}
          </div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px]">Quantità computo</div>
          <div>{fmt(qta_riga)} {riga.unita_misura || ""}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px]">Costo mat. Analisi totale</div>
          <div data-testid="recap-analisi-totale">€ {fmt(costoAnalisiTot)}</div>
        </div>
        <div className="col-span-2">
          <div className="text-slate-500 text-[10px]">Costo mat. INVILUPPO totale</div>
          <div className="font-semibold text-emerald-700 dark:text-emerald-400" data-testid="recap-inviluppo-totale">€ {fmt(costoInviluppoTot)}</div>
        </div>
      </div>
      <div className="text-[10px] italic text-slate-500 mt-2">
        Considera solo materiali generati da Analisi Prezzi. Materiali manuali esclusi.
      </div>
    </div>
  );
}


function MaterialiOrdiniForRiga({ riga, ordini, onChange, canEdit, highlightOrdine }) {
  const [matOpen, setMatOpen] = useState(false);
  const [ordOpen, setOrdOpen] = useState(false);
  const [editingMat, setEditingMat] = useState(null);
  const [editingOrd, setEditingOrd] = useState(null);
  const emptyMat = { descrizione: "", unita_misura: "", is_manual_quantita: false, coefficiente_per_unita: "1", quantita_manuale: "", prezzo_unitario: "" };
  const emptyOrd = { descrizione_materiale: "", quantita: "", prezzo_unitario: "", unita_misura: "", data_ordine: "", data_prevista_consegna: "", data_effettiva_consegna: "", stato: "da_ordinare", numero_ordine: "", note: "" };
  const [matForm, setMatForm] = useState(emptyMat);
  const [ordForm, setOrdForm] = useState(emptyOrd);
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt4 = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  // ---------- FASE controllo costi — Materiali XPWE gestionale ----------
  // Backend endpoint STEP A: GET/PATCH /api/righe/{id}/materiali-computo.
  // Nessuna formula ricalcolata lato client: si usano i valori del backend.
  const [mateComp, setMateComp] = useState(null);
  const [mateIncrEdit, setMateIncrEdit] = useState("");
  const [savingMate, setSavingMate] = useState(false);

  const loadMateComp = async () => {
    try {
      const { data } = await api.get(`/righe/${riga.id}/materiali-computo`);
      setMateComp(data);
      setMateIncrEdit(data.incremento_percent != null ? String(data.incremento_percent) : "");
    } catch (e) { console.warn("materiali-computo:", e?.message); }
  };
  useEffect(() => { loadMateComp(); }, [riga.id]);

  const saveMateIncremento = async (val) => {
    setSavingMate(true);
    try {
      const body = val === "" || val === null
        ? { incremento_percent: null }
        : { incremento_percent: Number(val) };
      const { data } = await api.patch(`/righe/${riga.id}/materiali-computo`, body);
      setMateComp(data);
      setMateIncrEdit(data.incremento_percent != null ? String(data.incremento_percent) : "");
      toast.success(val === "" || val === null
        ? "Materiali: ripristinato (nessun incremento gestionale)"
        : "Materiali: incremento salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingMate(false); }
  };
  const resetMateIncremento = () => saveMateIncremento(null);
  // Proposta Auto Materiali (STEP B.2):
  // Il backend restituisce `incremento_percent_auto` calcolato dalle regole
  // categoria/globali (priorità: categoria > globale > 0). L'override manuale
  // NON contamina la proposta AUTO (`is_incremento_manuale` è distinto).
  const mateAutoValue = mateComp?.incremento_percent_auto != null
    ? String(mateComp.incremento_percent_auto) : "0";
  const mateAutoSource = mateComp?.auto_source || "default";
  const mateSourceOK = mateComp && mateComp.source === "xpwe";

  // ------------------------------------------------------------------
  // FASE C.3 — AUTO INVILUPPO (single-row state)
  // GET /api/righe/{id}/inviluppo-auto → state='preview' | 'applied' | 'stale'
  // ------------------------------------------------------------------
  const [invAuto, setInvAuto] = useState(null);
  const loadInvAuto = async () => {
    try {
      const { data } = await api.get(`/righe/${riga.id}/inviluppo-auto`);
      setInvAuto(data);
    } catch (e) { console.warn("inviluppo-auto:", e?.message); }
  };
  useEffect(() => { loadInvAuto(); /* eslint-disable-line */ }, [riga.id]);


  const openNewMat = () => { setEditingMat(null); setMatForm(emptyMat); setMatOpen(true); };
  const openEditMat = (m) => { setEditingMat(m); setMatForm({
    descrizione: m.descrizione || "", unita_misura: m.unita_misura || "",
    is_manual_quantita: !!m.is_manual_quantita,
    coefficiente_per_unita: m.coefficiente_per_unita != null ? String(m.coefficiente_per_unita) : "1",
    quantita_manuale: m.quantita_manuale != null ? String(m.quantita_manuale) : "",
    prezzo_unitario: m.prezzo_unitario != null ? String(m.prezzo_unitario) : "",
  }); setMatOpen(true); };
  const submitMat = async (e) => {
    e.preventDefault();
    const p = { ...matForm };
    p.coefficiente_per_unita = Number(matForm.coefficiente_per_unita || 0);
    p.quantita_manuale = matForm.quantita_manuale === "" ? null : Number(matForm.quantita_manuale);
    p.prezzo_unitario = matForm.prezzo_unitario === "" ? null : Number(matForm.prezzo_unitario);
    Object.keys(p).forEach(k => { if (p[k] === "") p[k] = null; });
    try {
      if (editingMat) await api.patch(`/materiali-previsti/${editingMat.id}`, p);
      else await api.post(`/righe/${riga.id}/materiali`, p);
      toast.success(editingMat ? "Materiale aggiornato" : "Materiale aggiunto");
      setMatOpen(false); onChange();
    } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delMat = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/materiali-previsti/${id}`); onChange(); };
  const openNewOrd = () => { setEditingOrd(null); setOrdForm(emptyOrd); setOrdOpen(true); };
  const openEditOrd = (o) => { setEditingOrd(o); setOrdForm({
    descrizione_materiale: o.descrizione_materiale || "",
    quantita: o.quantita != null ? String(o.quantita) : "",
    prezzo_unitario: o.prezzo_unitario != null ? String(o.prezzo_unitario) : "",
    unita_misura: o.unita_misura || "",
    data_ordine: o.data_ordine || "",
    data_prevista_consegna: o.data_prevista_consegna || "",
    data_effettiva_consegna: o.data_effettiva_consegna || "",
    stato: o.stato || "da_ordinare", numero_ordine: o.numero_ordine || "", note: o.note || "",
  }); setOrdOpen(true); };
  const submitOrd = async (e) => {
    e.preventDefault();
    const p = { ...ordForm };
    Object.keys(p).forEach(k => { if (p[k] === "") p[k] = null; });
    ["quantita","prezzo_unitario"].forEach(k => { if (p[k] != null) p[k] = Number(p[k]); });
    try {
      if (editingOrd) await api.patch(`/ordini/${editingOrd.id}`, p);
      else await api.post(`/cantieri/${riga.cantiere_id}/ordini`, { ...p, riga_computo_id: riga.id });
      toast.success(editingOrd ? "Ordine aggiornato" : "Ordine creato");
      setOrdOpen(false); onChange();
    } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const patchOrd = async (id, stato) => { await api.patch(`/ordini/${id}`, { stato }); onChange(); };
  const delOrd = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/ordini/${id}`); onChange(); };
  return (
    <div className="space-y-4">
      {/* ==================================================================
          FASE controllo costi — DA COMPUTO / XPWE (Materiali)
          Convenzione INVILUPPO: quota materiale = 100% - IncMDO XPWE.
          Convive con "Materiali previsti" (Analisi Prezzi): quest'ultima
          resta dettaglio delle singole risorse. NESSUN doppio conteggio.
          ================================================================== */}
      <Card><CardContent className="p-6">
        <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold flex items-center gap-2 mb-3">
          <Package className="h-3.5 w-3.5" /> Materiali — Da computo / XPWE
        </div>
        {!mateComp ? (
          <div className="text-xs text-slate-500 italic px-2 py-2">Caricamento…</div>
        ) : !mateSourceOK ? (
          <div className="text-xs text-slate-500 italic px-2 py-2 border border-dashed border-slate-300 dark:border-slate-700 rounded" data-testid="mate-xpwe-unavailable">
            Quota materiale XPWE {mateComp.source === "invalid" ? "non valida" : "non disponibile"}
            {mateComp.source_reason ? <> — <span className="text-slate-600">{mateComp.source_reason}</span></> : null}.
          </div>
        ) : (
          <div className="border border-cyan-200 dark:border-cyan-900 rounded-lg p-3 space-y-2 bg-cyan-50/40 dark:bg-cyan-950/30" data-testid="mate-xpwe-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase text-slate-500">UM</div>
                <div className="font-mono font-semibold" data-testid="mate-xpwe-um">{mateComp.unita_misura || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Prezzo voce</div>
                <div className="font-mono font-semibold" data-testid="mate-xpwe-prezzo">€ {fmt4(mateComp.prezzo_unitario)}/{mateComp.unita_misura || "UM"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Incidenza MDO (XPWE)</div>
                <div className="font-mono font-semibold" data-testid="mate-xpwe-inc-mdo">{fmt(mateComp.inc_mdo_percent)}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Quota materiale (XPWE)</div>
                <div className="font-mono font-semibold text-cyan-700 dark:text-cyan-400" data-testid="mate-xpwe-quota">{fmt(mateComp.quota_materiale_percent)}%</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs pt-2 border-t border-cyan-200 dark:border-cyan-900">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase text-slate-500">Materiale originale (XPWE)</div>
                <div className="font-mono font-semibold" data-testid="mate-xpwe-orig">€ {fmt4(mateComp.materiale_originale_eur_per_um)}/{mateComp.unita_misura || "UM"}</div>
                <div className="text-[10px] text-slate-500">= prezzo × quota / 100</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase text-slate-500 mb-1">
                  Variazione Inviluppo % {mateComp.is_incremento_manuale ? <span className="text-amber-600 normal-case">(manuale)</span> : <span className="text-slate-400 normal-case">(default 0%)</span>}
                  {mateComp.incremento_source === "manual" && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-emerald-700 dark:text-emerald-400 border border-emerald-500/40 rounded px-1 py-[1px]" data-testid="mate-provenance-badge" title="Salvato manualmente. Protetto dagli apply massivi.">Modifica manuale</span>
                  )}
                  {mateComp.incremento_source === "auto_apply" && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-sky-700 dark:text-sky-400 border border-sky-500/40 rounded px-1 py-[1px]" data-testid="mate-provenance-badge" title="Applicato da regola. Verrà aggiornato al prossimo apply.">Applicato da regola</span>
                  )}
                  {mateComp.is_incremento_manuale && !mateComp.incremento_source && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-slate-600 dark:text-slate-400 border border-slate-400/40 rounded px-1 py-[1px]" data-testid="mate-provenance-badge" title="Valore preesistente. Protetto dagli apply massivi finché non lo confermi manualmente.">Valore esistente</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.1" value={mateIncrEdit}
                         onChange={(e) => setMateIncrEdit(e.target.value)}
                         disabled={!canEdit || savingMate}
                         className="h-8 font-mono" data-testid="mate-xpwe-incr-input" />
                  <Button size="sm" variant="default"
                          disabled={!canEdit || savingMate || mateIncrEdit === ""}
                          onClick={() => saveMateIncremento(mateIncrEdit)}
                          data-testid="mate-xpwe-incr-save">Salva</Button>
                  <Button size="sm" variant="outline"
                          disabled={!canEdit || savingMate}
                          onClick={() => setMateIncrEdit(mateAutoValue)}
                          title="Proposta automatica"
                          data-testid="mate-xpwe-incr-auto">Auto</Button>
                  {mateComp.is_incremento_manuale && (
                    <Button size="sm" variant="ghost"
                            disabled={!canEdit || savingMate}
                            onClick={resetMateIncremento}
                            title="Ripristina default (0%)"
                            data-testid="mate-xpwe-incr-reset">Reset</Button>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Proposta STEP B (legacy): <b className="font-mono" data-testid="mate-xpwe-auto-value">{mateAutoValue}%</b>
                  <span className="ml-1 text-slate-400" data-testid="mate-xpwe-auto-source">({mateAutoSource})</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs items-end pt-2 border-t border-cyan-200 dark:border-cyan-900">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Materiale stimato Inviluppo</div>
                <div className="font-mono font-semibold text-cyan-700 dark:text-cyan-400" data-testid="mate-xpwe-stim">€ {fmt4(mateComp.materiale_stimato_eur_per_um)}/{mateComp.unita_misura || "UM"}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase text-slate-500">Costo previsto totale (Q.tà computo × Materiale stimato)</div>
                <div className="font-mono font-bold text-lg text-cyan-700 dark:text-cyan-400" data-testid="mate-xpwe-costo-tot">€ {fmt(mateComp.costo_previsto_totale)}</div>
                <div className="text-[10px] text-slate-500">Q.tà computo: <span className="font-mono" data-testid="mate-xpwe-qta">{fmt(mateComp.quantita)} {mateComp.unita_misura || ""}</span></div>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 italic pt-1">
              Quota materiale XPWE = quota residua gestionale calcolata come 100% − incidenza MDO XPWE. Non modifica il computo originale.
              Convive con "Materiali previsti" (dettaglio Analisi Prezzi): nessun doppio conteggio automatico.
            </p>
          </div>
        )}

        {/* ---------- FASE C.3 — AUTO INVILUPPO (Materiali) ---------- */}
        <AutoInviluppoBlock inv={invAuto} section="mate" />
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3"><div className="text-xs uppercase tracking-widest text-amber-600 font-semibold flex items-center gap-2"><Package className="h-3.5 w-3.5" /> Materiali previsti per questa voce</div>{canEdit && <Button size="sm" variant="outline" onClick={openNewMat} data-testid="riga-add-mat-btn"><Plus className="h-3 w-3 mr-1" /> Materiale previsto</Button>}</div>
        {/* FASE 2C — Mini-card riepilogo materiali della voce, mostrata solo se
            esiste almeno un materiale generato da Analisi Prezzi. */}
        <MaterialiVoceRecap riga={riga} />
        <Table>
          <TableHeader><TableRow><TableHead>Descrizione</TableHead><TableHead>UM</TableHead><TableHead className="text-right">Coeff.</TableHead><TableHead className="text-right">Q.tà tot.</TableHead><TableHead className="text-right">Prezzo Analisi</TableHead><TableHead className="text-right">Costo Analisi</TableHead><TableHead className="text-right">Prezzo INVILUPPO</TableHead><TableHead className="text-right">Costo INVILUPPO</TableHead><TableHead className="text-right">Δ %</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{(riga.materiali_previsti || []).length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-4 text-slate-500">Nessun materiale previsto</TableCell></TableRow> : riga.materiali_previsti.map(m => {
            const isGen = !!m.source_analisi_risorsa_id;
            const pAnalisi = isGen ? m.prezzo_unitario_originale : null;
            const cAnalisi = isGen ? m.costo_previsto_originale : null;
            const pInv = m.prezzo_unitario;
            const cInv = m.costo_previsto;
            let delta = null;
            if (isGen && pAnalisi != null && Number(pAnalisi) !== 0) {
              delta = ((Number(pInv || 0) - Number(pAnalisi)) / Number(pAnalisi)) * 100;
            }
            return (
              <TableRow key={m.id} data-testid={`mat-row-${m.id}`}>
                <TableCell>
                  {m.descrizione}
                  {isGen && (
                    <Badge variant="outline" className="ml-2 text-[9px] uppercase text-amber-700 border-amber-500" data-testid={`mat-badge-analisi-${m.id}`}>
                      Da Analisi Prezzi
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{m.unita_misura || "—"}</TableCell>
                <TableCell className="text-right font-mono">{m.is_manual_quantita ? <span className="text-xs italic text-slate-500">Manuale</span> : fmt(m.coefficiente_per_unita)}</TableCell>
                <TableCell className="text-right font-mono">{fmt(m.quantita_complessiva)}</TableCell>
                <TableCell className="text-right font-mono text-slate-500" data-testid={`mat-prezzo-analisi-${m.id}`}>{isGen ? fmt(pAnalisi) : <span className="italic text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right font-mono text-slate-500" data-testid={`mat-costo-analisi-${m.id}`}>{isGen ? `€ ${fmt(cAnalisi)}` : <span className="italic text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right font-mono" data-testid={`mat-prezzo-inviluppo-${m.id}`}>{fmt(pInv)}</TableCell>
                <TableCell className="text-right font-mono font-semibold" data-testid={`mat-costo-inviluppo-${m.id}`}>€ {fmt(cInv)}</TableCell>
                <TableCell className={`text-right font-mono text-xs ${delta == null ? "text-slate-400" : delta > 0 ? "text-rose-600" : delta < 0 ? "text-emerald-600" : ""}`} data-testid={`mat-delta-${m.id}`}>
                  {delta == null ? "—" : `${delta > 0 ? "+" : ""}${fmt(delta)}%`}
                </TableCell>
                <TableCell className="text-right">
                  {canEdit && <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEditMat(m)} data-testid={`mat-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delMat(m.id)} data-testid={`mat-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>}
                </TableCell>
              </TableRow>
            );
          })}</TableBody>
        </Table>
      </CardContent></Card>
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3"><div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Ordini materiali collegati</div>{canEdit && <Button size="sm" variant="outline" onClick={openNewOrd} data-testid="riga-add-ord-btn"><Plus className="h-3 w-3 mr-1" /> Nuovo ordine</Button>}</div>
        <Table>
          <TableHeader><TableRow><TableHead>N.</TableHead><TableHead>Materiale</TableHead><TableHead>Q.tà</TableHead><TableHead>Consegna</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Costo</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{ordini.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-4 text-slate-500">Nessun ordine per questa voce</TableCell></TableRow> : ordini.map(o => (
            <TableRow key={o.id} className={highlightOrdine === o.id ? "bg-amber-100 dark:bg-amber-900/30" : ""} data-testid={`riga-ord-${o.id}`}>
              <TableCell className="font-mono text-xs">{o.numero_ordine || "—"}</TableCell>
              <TableCell>{o.descrizione_materiale}</TableCell>
              <TableCell className="font-mono">{fmt(o.quantita)}</TableCell>
              <TableCell className="text-xs">{o.data_prevista_consegna || "—"}</TableCell>
              <TableCell>{canEdit ? <Select value={o.stato} onValueChange={(v) => patchOrd(o.id, v)}><SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger><SelectContent>{STATI_ORD.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent></Select> : <Badge variant="outline">{o.stato}</Badge>}</TableCell>
              <TableCell className="text-right font-mono">€ {fmt(o.costo_totale)}</TableCell>
              <TableCell className="text-right">
                {canEdit && <div className="flex gap-1 justify-end">
                  <Button size="icon" variant="ghost" onClick={() => openEditOrd(o)} data-testid={`ord-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => delOrd(o.id)} data-testid={`ord-del-${o.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </div>}
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={matOpen} onOpenChange={setMatOpen}><DialogContent><DialogHeader><DialogTitle>{editingMat ? "Modifica materiale" : "Materiale previsto"}</DialogTitle><DialogDescription>Scegli la modalità di calcolo della quantità totale.</DialogDescription></DialogHeader>
        <form onSubmit={submitMat} className="space-y-3">
          <div><Label>Descrizione *</Label><Input required value={matForm.descrizione} onChange={(e) => setMatForm({ ...matForm, descrizione: e.target.value })} data-testid="riga-mat-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>UM</Label><Input value={matForm.unita_misura} onChange={(e) => setMatForm({ ...matForm, unita_misura: e.target.value })} /></div>
            <div><Label>Prezzo €/UM</Label><Input type="number" step="0.01" value={matForm.prezzo_unitario} onChange={(e) => setMatForm({ ...matForm, prezzo_unitario: e.target.value })} /></div>
          </div>
          <div className="border rounded-md p-3 space-y-2 bg-slate-50 dark:bg-slate-900">
            <Label className="text-xs uppercase text-amber-600 font-semibold">Modalità quantità</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setMatForm({ ...matForm, is_manual_quantita: false })} data-testid="mat-mode-auto" className={`flex-1 text-xs px-3 py-2 rounded border ${!matForm.is_manual_quantita ? "bg-amber-500 text-slate-950 font-semibold border-amber-500" : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"}`}>Automatica da coefficiente</button>
              <button type="button" onClick={() => setMatForm({ ...matForm, is_manual_quantita: true })} data-testid="mat-mode-manual" className={`flex-1 text-xs px-3 py-2 rounded border ${matForm.is_manual_quantita ? "bg-amber-500 text-slate-950 font-semibold border-amber-500" : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"}`}>Quantità manuale</button>
            </div>
            {matForm.is_manual_quantita ? (
              <div><Label>Quantità totale manuale *</Label><Input type="number" step="0.0001" required value={matForm.quantita_manuale} onChange={(e) => setMatForm({ ...matForm, quantita_manuale: e.target.value })} data-testid="mat-qta-manuale" /><p className="text-[10px] text-slate-500 mt-1">Coefficiente ignorato in questa modalità.</p></div>
            ) : (
              <div><Label>Coefficiente per unità *</Label><Input type="number" step="0.0001" required value={matForm.coefficiente_per_unita} onChange={(e) => setMatForm({ ...matForm, coefficiente_per_unita: e.target.value })} data-testid="riga-mat-coeff" /><p className="text-[10px] text-slate-500 mt-1">Q.tà totale = Q.tà voce ({fmt(riga.quantita)}) × Coefficiente</p></div>
            )}
          </div>
          <DialogFooter><Button type="submit" data-testid="riga-mat-submit" className="bg-amber-500 text-slate-950 font-semibold">{editingMat ? "Salva" : "Aggiungi"}</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>
      <Dialog open={ordOpen} onOpenChange={setOrdOpen}><DialogContent><DialogHeader><DialogTitle>{editingOrd ? "Modifica ordine" : "Nuovo ordine collegato"}</DialogTitle><DialogDescription>Tutti i collegamenti (cantiere/riga/computo) sono preservati.</DialogDescription></DialogHeader>
        <form onSubmit={submitOrd} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Materiale *</Label><Input required value={ordForm.descrizione_materiale} onChange={(e) => setOrdForm({ ...ordForm, descrizione_materiale: e.target.value })} data-testid="riga-ord-desc" /></div>
          <div><Label>Q.tà *</Label><Input type="number" step="0.01" required value={ordForm.quantita} onChange={(e) => setOrdForm({ ...ordForm, quantita: e.target.value })} data-testid="riga-ord-qta" /></div>
          <div><Label>UM</Label><Input value={ordForm.unita_misura} onChange={(e) => setOrdForm({ ...ordForm, unita_misura: e.target.value })} /></div>
          <div><Label>Prezzo €/UM</Label><Input type="number" step="0.01" value={ordForm.prezzo_unitario} onChange={(e) => setOrdForm({ ...ordForm, prezzo_unitario: e.target.value })} /></div>
          <div><Label>N. ordine</Label><Input value={ordForm.numero_ordine} onChange={(e) => setOrdForm({ ...ordForm, numero_ordine: e.target.value })} /></div>
          <div><Label>Data ordine</Label><Input type="date" value={ordForm.data_ordine} onChange={(e) => setOrdForm({ ...ordForm, data_ordine: e.target.value })} /></div>
          <div><Label>Data prevista</Label><Input type="date" value={ordForm.data_prevista_consegna} onChange={(e) => setOrdForm({ ...ordForm, data_prevista_consegna: e.target.value })} /></div>
          <div><Label>Data effettiva</Label><Input type="date" value={ordForm.data_effettiva_consegna} onChange={(e) => setOrdForm({ ...ordForm, data_effettiva_consegna: e.target.value })} /></div>
          <div className="col-span-2"><Label>Stato</Label><Select value={ordForm.stato} onValueChange={(v) => setOrdForm({ ...ordForm, stato: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATI_ORD.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={ordForm.note} onChange={(e) => setOrdForm({ ...ordForm, note: e.target.value })} /></div>
          <DialogFooter className="col-span-2"><Button type="submit" data-testid="riga-ord-submit" className="bg-amber-500 text-slate-950 font-semibold">{editingOrd ? "Salva" : "Crea"}</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>
    </div>
  );
}

function ManodoperaForRiga({ riga, manEff, rilievi, onChange, canEdit }) {
  const [prev, setPrev] = useState(false);
  const [eff, setEff] = useState(false);
  const emptyP = { id: null, mode: "ore", tipo_operaio: "", num_operai: "", ore_previste: "", costo_orario: "", subappaltatore_id: "", quantita_prevista: "", prezzo_contrattuale: "" };
  const emptyE = { id: null, mode: "ore", operaio_nome: "", data: new Date().toISOString().slice(0,10), ore: "8", costo_orario: "35", subappaltatore_id: "", tipo_operaio: "", num_operai: "1", quantita_rilevata: "", prezzo_contrattuale: "", note: "" };
  const [pF, setPF] = useState(emptyP);
  const [eF, setEF] = useState(emptyE);
  const [subs, setSubs] = useState([]);
  // Multi-dipendente (anagrafica subappaltatori/dipendenti) — solo per nuove registrazioni "A ore"
  const _uidRD = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const emptyLine = () => ({ _key: _uidRD(), dipendente_id: "", tipo_operaio: "", ore: "8", costo_orario: "35" });
  const [anagSubs, setAnagSubs] = useState([]);
  // Subappaltatori assegnati alla Categoria Operativa della riga (Task 4A) —
  // usati come SUGGERIMENTO nel dialog Manodopera prevista A quantità.
  const [catSubs, setCatSubs] = useState([]); // [{id, nome}]
  const [anagDips, setAnagDips] = useState([]);
  const [anagSubId, setAnagSubId] = useState("none");
  const [lines, setLines] = useState([emptyLine()]);
  const [savingMulti, setSavingMulti] = useState(false);
  // MDO da computo / XPWE (terza modalità di stima manodopera prevista)
  const [mdoComp, setMdoComp] = useState(null);
  // FASE C.3 — Auto INVILUPPO per la riga (READ-ONLY)
  const [invAuto, setInvAuto] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/righe/${riga.id}/inviluppo-auto`);
        setInvAuto(data);
      } catch (e) { console.warn("inviluppo-auto (manodopera):", e?.message); }
    })();
  }, [riga.id]);
  const [mdoIncrEdit, setMdoIncrEdit] = useState("");
  const [savingMdo, setSavingMdo] = useState(false);
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt4 = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const um = riga.unita_misura || "—";

  const loadMdoComp = async () => {
    try {
      const { data } = await api.get(`/righe/${riga.id}/mdo-computo`);
      setMdoComp(data);
      setMdoIncrEdit(data.incremento_percent != null ? String(data.incremento_percent) : "");
    } catch (e) { console.warn("mdo-computo:", e?.message); }
  };
  useEffect(() => { loadMdoComp(); }, [riga.id]);

  const saveMdoIncremento = async (val) => {
    setSavingMdo(true);
    try {
      const body = val === "" || val === null ? { incremento_percent: null } : { incremento_percent: Number(val) };
      const { data } = await api.patch(`/righe/${riga.id}/mdo-computo`, body);
      setMdoComp(data);
      setMdoIncrEdit(data.incremento_percent != null ? String(data.incremento_percent) : "");
      toast.success(val === "" || val === null ? "Ripristinato incremento automatico" : "Incremento salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingMdo(false); }
  };
  const resetMdoIncremento = () => saveMdoIncremento(null);

  const ensureSubs = async () => {
    if (subs.length) return;
    try { const { data } = await api.get(`/anagrafiche/subappaltatori`); setSubs(data || []); }
    catch (e) { console.warn("subs:", e?.message); }
  };
  const ensureAnagSubs = async () => {
    if (anagSubs.length) return;
    try { const { data } = await api.get(`/anagrafiche/subappaltatori`); setAnagSubs(data || []); }
    catch (e) { console.warn("anagSubs:", e?.message); }
  };
  useEffect(() => {
    if (anagSubId && anagSubId !== "none") {
      api.get("/dipendenti-subappaltatori", { params: { subappaltatore_id: anagSubId } })
        .then(r => setAnagDips(r.data || [])).catch(() => setAnagDips([]));
    } else { setAnagDips([]); }
    setLines([emptyLine()]);
  }, [anagSubId]);
  const openPrev = async (m = null) => {
    await ensureSubs();
    // Carica i sub assegnati alla categoria della riga (Task 4A) — SOLO in create
    // e SOLO se il campo è vuoto. Se la categoria ha esattamente 1 sub → preselect
    // + autofill prezzo. Se >1 → nessuna preselezione, ma li useremo come suggeriti.
    let suggested = [];
    let preselectSubId = null;
    if (!m && riga?.categoria_operativa_id && riga?.computo_id) {
      try {
        const { data } = await api.get(`/computi/${riga.computo_id}/categorie-subappaltatori`);
        const cat = (data?.categorie || []).find(c => c.categoria_id === riga.categoria_operativa_id);
        suggested = (cat?.subappaltatori || []).map(s => ({ id: s.id, nome: s.nome }));
        if (suggested.length === 1) preselectSubId = suggested[0].id;
      } catch (e) { console.warn("cat-subs suggerimento:", e?.message); }
    }
    setCatSubs(suggested);
    if (m) setPF({
      id: m.id, mode: m.mode || "ore",
      tipo_operaio: m.tipo_operaio || "", num_operai: m.num_operai ?? "",
      ore_previste: m.ore_previste != null ? String(m.ore_previste) : "",
      costo_orario: m.costo_orario != null ? String(m.costo_orario) : "",
      // Preselect via nuova anagrafica; fallback su legacy id per compat storica.
      // In EDIT: rispettiamo sempre il subappaltatore salvato (no override da categoria).
      subappaltatore_id: m.subappaltatore_anag_id || m.subappaltatore_id || "",
      quantita_prevista: m.quantita_prevista != null ? String(m.quantita_prevista) : "",
      prezzo_contrattuale: m.prezzo_contrattuale != null ? String(m.prezzo_contrattuale) : "",
    });
    else {
      const base = { ...emptyP };
      if (preselectSubId) {
        base.mode = "quantita";
        base.subappaltatore_id = preselectSubId;
      }
      setPF(base);
      // se abbiamo preselezionato → autofill prezzo esistente per (sub, riga)
      if (preselectSubId) autoFillPrezzo(preselectSubId, "prev");
    }
    setPrev(true);
  };
  const openEff = async (m = null) => {
    await ensureSubs();
    await ensureAnagSubs();
    if (m) setEF({
      id: m.id, mode: "ore",
      operaio_nome: m.operaio_nome || "", data: m.data,
      ore: String(m.ore ?? ""), costo_orario: String(m.costo_orario ?? ""),
      subappaltatore_id: m.subappaltatore_anag_id || m.subappaltatore_id || "",
      tipo_operaio: m.tipo_operaio || "",
      num_operai: m.num_operai != null ? String(m.num_operai) : "1",
      quantita_rilevata: "", prezzo_contrattuale: "", note: m.note || "",
    });
    else {
      setEF(emptyE);
      setAnagSubId("none");
      setLines([emptyLine()]);
    }
    setEff(true);
  };
  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (i) => setLines(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : lines);
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const onPickLineDip = (i, v) => {
    const dip = anagDips.find(d => d.id === v);
    updateLine(i, { dipendente_id: v, tipo_operaio: dip?.ruolo || lines[i].tipo_operaio || "" });
  };
  const usedLineDipIds = new Set(lines.map(l => l.dipendente_id).filter(Boolean));
  const delMane = async (id) => {
    if (!confirm("Eliminare questa registrazione?")) return;
    try { await api.delete(`/manodopera-effettiva/${id}`); toast.success("Eliminata"); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const autoFillPrezzo = async (subId, target) => {
    if (!subId) return;
    try {
      const { data } = await api.get(`/subappaltatori/${subId}/prezzi`);
      const match = (data || []).find(p => p.riga_computo_id === riga.id);
      if (match) {
        const v = String(match.prezzo_unitario);
        if (target === "prev") setPF(f => ({ ...f, prezzo_contrattuale: v }));
        else setEF(f => ({ ...f, prezzo_contrattuale: v }));
      }
    } catch (e) { console.warn("autofill prezzo:", e?.message); }
  };

  const savePrev = async (e) => {
    e.preventDefault();
    const isEdit = !!pF.id;
    let body;
    if (pF.mode === "ore") {
      if (!pF.tipo_operaio) { toast.error("Tipo operaio richiesto"); return; }
      body = { mode: "ore", tipo_operaio: pF.tipo_operaio };
      if (pF.num_operai !== "") body.num_operai = parseInt(pF.num_operai);
      if (pF.ore_previste !== "") body.ore_previste = Number(pF.ore_previste);
      if (pF.costo_orario !== "") body.costo_orario = Number(pF.costo_orario);
    } else {
      if (!pF.subappaltatore_id || !pF.quantita_prevista || !pF.prezzo_contrattuale) {
        toast.error("Compila subappaltatore, quantità e prezzo"); return;
      }
      body = {
        mode: "quantita",
        subappaltatore_id: pF.subappaltatore_id,
        unita_misura: riga.unita_misura || null,
        quantita_prevista: Number(pF.quantita_prevista),
        prezzo_contrattuale: Number(pF.prezzo_contrattuale),
      };
    }
    try {
      if (isEdit) await api.patch(`/manodopera-prevista/${pF.id}`, body);
      else await api.post(`/righe/${riga.id}/manodopera`, body);
      toast.success(isEdit ? "Manodopera prevista aggiornata" : "Manodopera prevista aggiunta");
      setPrev(false); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const delPrev = async (id) => {
    if (!confirm("Eliminare questa previsione?")) return;
    try { await api.delete(`/manodopera-prevista/${id}`); toast.success("Eliminata"); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const saveEff = async (e) => {
    e.preventDefault();
    if (eF.mode === "ore") {
      // NUOVI record: multi-dipendente. In edit: legacy single-row.
      if (!eF.id) {
        if (anagSubId === "none") { toast.error("Seleziona un subappaltatore"); return; }
        const valid = lines.filter(l => l.dipendente_id && Number(l.ore) > 0 && Number(l.costo_orario) > 0);
        if (valid.length === 0) { toast.error("Aggiungi almeno un dipendente con ore ed €/h"); return; }
        if (new Set(valid.map(l => l.dipendente_id)).size !== valid.length) { toast.error("Dipendente ripetuto"); return; }
        setSavingMulti(true);
        try {
          for (const l of valid) {
            const dip = anagDips.find(d => d.id === l.dipendente_id);
            await api.post(`/cantieri/${riga.cantiere_id}/manodopera-effettiva`, {
              operaio_nome: dip ? `${dip.cognome} ${dip.nome}`.trim() : "—",
              data: eF.data,
              ore: Number(l.ore), costo_orario: Number(l.costo_orario),
              riga_computo_id: riga.id, note: eF.note || null,
              tipo_operaio: l.tipo_operaio || null,
              num_operai: 1,
              dipendente_id: l.dipendente_id,
            });
          }
          toast.success(`${valid.length} registrazion${valid.length === 1 ? "e creata" : "i create"}`);
          setEff(false); onChange();
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
        finally { setSavingMulti(false); }
        return;
      }
      // EDIT: comportamento legacy invariato
      if (!eF.operaio_nome) { toast.error("Operaio richiesto"); return; }
      const body = {
        operaio_nome: eF.operaio_nome, data: eF.data,
        ore: Number(eF.ore), costo_orario: Number(eF.costo_orario),
        riga_computo_id: riga.id, note: eF.note || null,
        subappaltatore_id: eF.subappaltatore_id || null,
        tipo_operaio: eF.tipo_operaio || null,
        num_operai: eF.num_operai !== "" ? parseInt(eF.num_operai) : null,
      };
      try {
        await api.patch(`/manodopera-effettiva/${eF.id}`, body);
        toast.success("Registrazione aggiornata"); setEff(false); onChange();
      } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    } else {
      if (!eF.subappaltatore_id || !eF.quantita_rilevata) { toast.error("Compila subappaltatore e quantità"); return; }
      try {
        await api.post(`/righe/${riga.id}/rilievi`, {
          subappaltatore_id: eF.subappaltatore_id,
          data_rilievo: eF.data,
          quantita_rilevata: Number(eF.quantita_rilevata),
          note: eF.note || null,
        });
        if (eF.prezzo_contrattuale) {
          const existing = (await api.get(`/subappaltatori/${eF.subappaltatore_id}/prezzi`)).data.find(p => p.riga_computo_id === riga.id);
          if (existing) await api.patch(`/prezzi-contrattuali/${existing.id}`, { prezzo_unitario: Number(eF.prezzo_contrattuale) });
          else await api.post(`/subappaltatori/${eF.subappaltatore_id}/prezzi`, { cantiere_id: riga.cantiere_id, riga_computo_id: riga.id, prezzo_unitario: Number(eF.prezzo_contrattuale) });
        }
        toast.success("Registrato (visibile in Manodopera, Rilievi, SAL)"); setEff(false); onChange();
      } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    }
  };
  const [rilievoEdit, setRilievoEdit] = useState(null);
  const openRilievoEdit = async (r) => {
    await ensureSubs();
    setRilievoEdit({
      id: r.id,
      // Preselect via nuova anagrafica; fallback su legacy id
      subappaltatore_id: r.subappaltatore_anag_id || r.subappaltatore_id || "",
      data_rilievo: r.data_rilievo,
      quantita_rilevata: String(r.quantita_rilevata ?? ""),
      note: r.note || "",
    });
  };
  const saveRilievoEdit = async (e) => {
    e.preventDefault();
    if (!rilievoEdit) return;
    const { id, ...body } = rilievoEdit;
    try {
      await api.patch(`/rilievi/${id}`, {
        subappaltatore_id: body.subappaltatore_id || null,
        data_rilievo: body.data_rilievo,
        quantita_rilevata: Number(body.quantita_rilevata),
        note: body.note || null,
      });
      toast.success("Registrazione aggiornata"); setRilievoEdit(null); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const delRilievo = async (id) => {
    if (!confirm("Eliminare questa registrazione?")) return;
    try { await api.delete(`/rilievi/${id}`); toast.success("Eliminata"); onChange(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const manp = riga.manodopera_prevista || [];
  const manpOre = manp.filter(m => (m.mode || "ore") === "ore");
  const manpQta = manp.filter(m => m.mode === "quantita");

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold flex items-center gap-2"><Users2 className="h-3.5 w-3.5" /> Manodopera prevista</div>
          {canEdit && <Button size="sm" variant="outline" onClick={() => openPrev(null)} data-testid="riga-add-manp"><Plus className="h-3 w-3 mr-1" /> Prevista</Button>}
        </div>

        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-1 mb-1">A ore</div>
        <Table>
          <TableHeader><TableRow><TableHead>Tipo operaio</TableHead><TableHead className="text-right">N.</TableHead><TableHead className="text-right">Ore</TableHead><TableHead className="text-right">€/h</TableHead><TableHead className="text-right">Costo prev.</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>{manpOre.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-3 text-slate-500 text-sm">—</TableCell></TableRow> : manpOre.map(m => (
            <TableRow key={m.id} data-testid={`manp-ore-${m.id}`}>
              <TableCell>
                {m.tipo_operaio || "—"}
                {m.source_analisi_risorsa_id && (
                  <Badge variant="outline" className="ml-2 text-[9px] uppercase text-amber-700 border-amber-500" data-testid={`manp-badge-analisi-${m.id}`}>
                    Da Analisi Prezzi
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right">{m.num_operai ?? "—"}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.ore_previste)}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.costo_orario)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">€ {fmt(m.costo_previsto)}</TableCell>
              <TableCell className="text-right">{canEdit && <><Button size="icon" variant="ghost" onClick={() => openPrev(m)} data-testid={`manp-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button><Button size="icon" variant="ghost" onClick={() => delPrev(m.id)} data-testid={`manp-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>

        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-4 mb-1">A quantità</div>
        <Table>
          <TableHeader><TableRow><TableHead>Subappaltatore</TableHead><TableHead>UM</TableHead><TableHead className="text-right">Q.tà prevista</TableHead><TableHead className="text-right">Prezzo €/UM</TableHead><TableHead className="text-right">Costo prev.</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>{manpQta.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-3 text-slate-500 text-sm">—</TableCell></TableRow> : manpQta.map(m => (
            <TableRow key={m.id} data-testid={`manp-qta-${m.id}`}>
              <TableCell>{m.subappaltatore_nome || "—"}</TableCell>
              <TableCell>{m.unita_misura || um}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.quantita_prevista)}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.prezzo_contrattuale)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">€ {fmt(m.costo_previsto)}</TableCell>
              <TableCell className="text-right">{canEdit && <><Button size="icon" variant="ghost" onClick={() => openPrev(m)} data-testid={`manp-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button><Button size="icon" variant="ghost" onClick={() => delPrev(m.id)} data-testid={`manp-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
        <p className="text-[10px] text-slate-500 mt-2 italic">Il Prezzo €/UM è fonte unica in “Prezzi contrattuali subappaltatore”: modificarlo qui aggiorna anche la pagina Subappaltatori (e viceversa).</p>

        {/* ---------- DA COMPUTO / XPWE ---------- */}
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-6 mb-1">Da computo / XPWE</div>
        {!mdoComp ? (
          <div className="text-xs text-slate-500 italic px-2 py-2">Caricamento…</div>
        ) : mdoComp.source !== "xpwe" ? (
          <div className="text-xs text-slate-500 italic px-2 py-2 border border-dashed border-slate-300 dark:border-slate-700 rounded" data-testid="mdo-xpwe-unavailable">
            Dato non disponibile — la voce di computo non contiene l'incidenza manodopera (Inc MDO) dell'XPWE.
          </div>
        ) : (
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2 bg-slate-50/40 dark:bg-slate-900/40" data-testid="mdo-xpwe-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><div className="text-[10px] uppercase text-slate-500">UM</div><div className="font-mono font-semibold" data-testid="mdo-xpwe-um">{mdoComp.unita_misura || "—"}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Prezzo voce</div><div className="font-mono font-semibold" data-testid="mdo-xpwe-prezzo">€ {fmt4(mdoComp.prezzo_unitario)}/{mdoComp.unita_misura || "UM"}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Incidenza MDO (XPWE)</div><div className="font-mono font-semibold" data-testid="mdo-xpwe-inc">{fmt(mdoComp.inc_mdo_percent)}%</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">MDO originale (XPWE)</div><div className="font-mono font-semibold" data-testid="mdo-xpwe-orig">€ {fmt4(mdoComp.mdo_originale_eur_per_um)}/{mdoComp.unita_misura || "UM"}</div></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs items-end pt-2 border-t border-slate-200 dark:border-slate-700">
              <div>
                <div className="text-[10px] uppercase text-slate-500 mb-1">Incremento Inviluppo % {mdoComp.is_incremento_manuale ? <span className="text-amber-600 normal-case">(manuale)</span> : <span className="text-slate-400 normal-case">(auto)</span>}
                  {mdoComp.incremento_source === "manual" && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-emerald-700 dark:text-emerald-400 border border-emerald-500/40 rounded px-1 py-[1px]" data-testid="mdo-provenance-badge" title="Salvato manualmente. Protetto dagli apply massivi.">Modifica manuale</span>
                  )}
                  {mdoComp.incremento_source === "auto_apply" && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-sky-700 dark:text-sky-400 border border-sky-500/40 rounded px-1 py-[1px]" data-testid="mdo-provenance-badge" title="Applicato da regola. Verrà aggiornato al prossimo apply.">Applicato da regola</span>
                  )}
                  {mdoComp.is_incremento_manuale && !mdoComp.incremento_source && (
                    <span className="ml-2 text-[9px] normal-case font-medium text-slate-600 dark:text-slate-400 border border-slate-400/40 rounded px-1 py-[1px]" data-testid="mdo-provenance-badge" title="Valore preesistente. Protetto dagli apply massivi finché non lo confermi manualmente.">Valore esistente</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.1" value={mdoIncrEdit} onChange={(e) => setMdoIncrEdit(e.target.value)}
                         disabled={!canEdit || savingMdo}
                         className="h-8 font-mono" data-testid="mdo-xpwe-incr-input" />
                  <Button size="sm" variant="default" disabled={!canEdit || savingMdo || mdoIncrEdit === ""} onClick={() => saveMdoIncremento(mdoIncrEdit)} data-testid="mdo-xpwe-incr-save">Salva</Button>
                  {mdoComp.is_incremento_manuale && <Button size="sm" variant="ghost" disabled={!canEdit || savingMdo} onClick={resetMdoIncremento} title="Ripristina automatico" data-testid="mdo-xpwe-incr-reset">Auto</Button>}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Proposta STEP B (legacy): <b className="font-mono">{fmt(mdoComp.incremento_percent_auto)}%</b></div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">MDO stimata Inviluppo</div>
                <div className="font-mono font-semibold text-amber-600" data-testid="mdo-xpwe-stim">€ {fmt4(mdoComp.mdo_stimata_eur_per_um)}/{mdoComp.unita_misura || "UM"}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase text-slate-500">Costo previsto totale (Q.tà computo × MDO stimata)</div>
                <div className="font-mono font-bold text-lg text-amber-700 dark:text-amber-500" data-testid="mdo-xpwe-costo-tot">€ {fmt(mdoComp.costo_previsto_totale)}</div>
                <div className="text-[10px] text-slate-500">Q.tà computo: <span className="font-mono">{fmt(mdoComp.quantita)} {mdoComp.unita_misura || ""}</span></div>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 italic pt-1">MDO originale XPWE è immutabile. L'Incremento è gestionale Inviluppo e non altera la voce di computo, i SAL, i rilievi o le registrazioni giornaliere.</p>
          </div>
        )}

        {/* ---------- FASE C.3 — AUTO INVILUPPO (MDO) ---------- */}
        <AutoInviluppoBlock inv={invAuto} section="mdo" />
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Manodopera effettiva registrata</div>
          {canEdit && <Button size="sm" variant="outline" onClick={() => openEff(null)} data-testid="riga-add-mane"><Plus className="h-3 w-3 mr-1" /> Registra</Button>}
        </div>

        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-1 mb-1">A ore</div>
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Subappaltatore</TableHead><TableHead>Tipo operaio</TableHead><TableHead className="text-right">N.</TableHead><TableHead className="text-right">Ore</TableHead><TableHead className="text-right">€/h</TableHead><TableHead className="text-right">Totale</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>{manEff.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-3 text-slate-500 text-sm">—</TableCell></TableRow> : manEff.map(m => (
            <TableRow key={m.id} data-testid={`mane-ore-${m.id}`}>
              <TableCell className="font-mono text-xs">{m.data}</TableCell>
              <TableCell className="text-sm">{m.subappaltatore_nome || "—"}</TableCell>
              <TableCell className="text-sm">{m.tipo_operaio || m.operaio_nome}</TableCell>
              <TableCell className="text-right">{m.num_operai ?? 1}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.ore)}</TableCell>
              <TableCell className="text-right font-mono">{fmt(m.costo_orario)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">€ {fmt(m.costo_totale)}</TableCell>
              <TableCell className="text-right">{canEdit && !m.sal_ore_id && <><Button size="icon" variant="ghost" onClick={() => openEff(m)} data-testid={`mane-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button><Button size="icon" variant="ghost" onClick={() => delMane(m.id)} data-testid={`mane-del-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></>}{m.sal_ore_id && <span className="text-[10px] text-slate-500">SAL emesso</span>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>

        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mt-4 mb-1">A quantità <span className="normal-case font-normal text-slate-400">(stesso record dei Rilievi/SAL)</span></div>
        <Table>
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Subappaltatore</TableHead><TableHead>UM</TableHead><TableHead className="text-right">Q.tà rilevata</TableHead><TableHead className="text-right">Prezzo €/UM</TableHead><TableHead className="text-right">Importo</TableHead><TableHead className="w-16"></TableHead></TableRow></TableHeader>
          <TableBody>{(rilievi || []).length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-3 text-slate-500 text-sm">—</TableCell></TableRow> : rilievi.map(r => {
            const prezzo = manpQta.find(m => m.subappaltatore_id === r.subappaltatore_id)?.prezzo_contrattuale ?? null;
            const imp = prezzo != null ? Number(r.quantita_rilevata) * Number(prezzo) : null;
            const inSal = !!r.sal_id;
            return (
              <TableRow key={r.id} data-testid={`mane-qta-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.data_rilievo}</TableCell>
                <TableCell>{r.subappaltatore_nome || "—"}</TableCell>
                <TableCell>{um}</TableCell>
                <TableCell className="text-right font-mono">{fmt(r.quantita_rilevata)}</TableCell>
                <TableCell className="text-right font-mono">{fmt(prezzo)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{imp != null ? `€ ${fmt(imp)}` : "—"}</TableCell>
                <TableCell className="text-right">
                  {inSal ? (
                    <Badge variant="outline" className="text-[9px] border-amber-500 text-amber-700" data-testid={`mane-qta-sal-${r.id}`} title={`SAL ${r.sal_numero}${r.sal_nome ? ' · '+r.sal_nome : ''}`}>Già in SAL</Badge>
                  ) : (canEdit && <>
                    <Button size="icon" variant="ghost" onClick={() => openRilievoEdit(r)} data-testid={`mane-qta-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delRilievo(r.id)} data-testid={`mane-qta-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </>)}
                </TableCell>
              </TableRow>
            );
          })}</TableBody>
        </Table>
        <p className="text-[10px] text-slate-500 mt-2 italic">Le registrazioni A quantità sono lo stesso record dei Rilievi (fonte unica): visibili qui, in tab Rilievi, in Subappaltatori e nel SAL.</p>
      </CardContent></Card>

      <Dialog open={prev} onOpenChange={setPrev}><DialogContent><DialogHeader><DialogTitle>{pF.id ? "Modifica manodopera prevista" : "Manodopera prevista"}</DialogTitle><DialogDescription>Contabilizzazione previsione.</DialogDescription></DialogHeader>
        <form onSubmit={savePrev} className="space-y-3">
          {!pF.id && (
            <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 w-fit">
              <button type="button" onClick={() => setPF({ ...pF, mode: "ore" })} data-testid="manp-mode-ore" className={`px-3 py-1 text-xs font-semibold rounded ${pF.mode==="ore" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>A ore</button>
              <button type="button" onClick={() => setPF({ ...pF, mode: "quantita" })} data-testid="manp-mode-qta" className={`px-3 py-1 text-xs font-semibold rounded ${pF.mode==="quantita" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>A quantità</button>
            </div>
          )}
          {pF.mode === "ore" ? <>
            <div><Label>Tipo operaio *</Label><Input required value={pF.tipo_operaio} onChange={(e) => setPF({ ...pF, tipo_operaio: e.target.value })} data-testid="riga-manp-tipo" /></div>
            <div className="grid grid-cols-3 gap-3"><div><Label>N.</Label><Input type="number" value={pF.num_operai} onChange={(e) => setPF({ ...pF, num_operai: e.target.value })} /></div><div><Label>Ore</Label><Input type="number" step="0.5" value={pF.ore_previste} onChange={(e) => setPF({ ...pF, ore_previste: e.target.value })} data-testid="riga-manp-ore" /></div><div><Label>€/h</Label><Input type="number" step="0.01" value={pF.costo_orario} onChange={(e) => setPF({ ...pF, costo_orario: e.target.value })} data-testid="riga-manp-eh" /></div></div>
          </> : <>
            <div className="text-xs bg-slate-50 dark:bg-slate-900 rounded p-2">Q.tà computo: <b className="font-mono">{fmt(riga.quantita)} {um}</b> · UM: <b>{um}</b></div>
            <div><Label>Subappaltatore *</Label>
              <Select value={pF.subappaltatore_id} onValueChange={(v) => { setPF({ ...pF, subappaltatore_id: v }); autoFillPrezzo(v, "prev"); }}>
                <SelectTrigger data-testid="manp-sub"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {catSubs.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-semibold">Assegnati alla categoria</div>
                      {catSubs.map(s => (
                        <SelectItem key={`cat-${s.id}`} value={s.id} data-testid={`manp-sub-cat-${s.id}`}>
                          {s.nome}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-t border-slate-200 dark:border-slate-800 mt-1">Altri</div>
                    </>
                  )}
                  {subs.filter(s => !catSubs.some(c => c.id === s.id)).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!pF.id && catSubs.length > 1 && (
                <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1">
                  Categoria con più subappaltatori — scegli manualmente.
                </p>
              )}
              {!pF.id && catSubs.length === 1 && pF.subappaltatore_id === catSubs[0].id && (
                <p className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-1">
                  Preselezionato dalla categoria — modificabile.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Q.tà prevista ({um}) *</Label><Input type="number" step="0.0001" required value={pF.quantita_prevista} onChange={(e) => setPF({ ...pF, quantita_prevista: e.target.value })} data-testid="manp-qta" /></div>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <Label>Prezzo contrattuale €/UM *</Label>
                  {mdoComp && mdoComp.source === "xpwe" && mdoComp.mdo_stimata_eur_per_um != null && (
                    <button
                      type="button"
                      onClick={() => setPF({ ...pF, prezzo_contrattuale: String(mdoComp.mdo_stimata_eur_per_um) })}
                      title={`Precompila con la MDO stimata INVILUPPO corrente: € ${fmt4(mdoComp.mdo_stimata_eur_per_um)}/${mdoComp.unita_misura || um}`}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded border border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25"
                      data-testid="manp-prezzo-usa-mdo"
                    >
                      Usa MDO INVILUPPO (€ {fmt4(mdoComp.mdo_stimata_eur_per_um)})
                    </button>
                  )}
                </div>
                <Input type="number" step="0.0001" required value={pF.prezzo_contrattuale} onChange={(e) => setPF({ ...pF, prezzo_contrattuale: e.target.value })} data-testid="manp-prezzo" />
              </div>
            </div>
            {pF.quantita_prevista && pF.prezzo_contrattuale && <div className="text-xs">Costo previsto: <b className="text-amber-600 font-mono">€ {fmt(Number(pF.quantita_prevista)*Number(pF.prezzo_contrattuale))}</b></div>}
            <p className="text-[10px] text-slate-500 italic">Il prezzo viene salvato in “Prezzi contrattuali subappaltatore” e sincronizzato automaticamente con la pagina Subappaltatori.</p>
          </>}
          <DialogFooter><Button type="submit" data-testid="riga-manp-submit" className="bg-amber-500 text-slate-950 font-semibold">{pF.id ? "Salva" : "Aggiungi"}</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>

      <Dialog open={eff} onOpenChange={setEff}><DialogContent><DialogHeader><DialogTitle>Registrazione giornaliera</DialogTitle><DialogDescription>A quantità = stesso record di Rilievi/SAL (fonte unica).</DialogDescription></DialogHeader>
        <form onSubmit={saveEff} className="space-y-3">
          <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 w-fit">
            <button type="button" onClick={() => setEF({ ...eF, mode: "ore" })} data-testid="mane-mode-ore" className={`px-3 py-1 text-xs font-semibold rounded ${eF.mode==="ore" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>A ore</button>
            <button type="button" onClick={() => setEF({ ...eF, mode: "quantita" })} data-testid="mane-mode-qta" className={`px-3 py-1 text-xs font-semibold rounded ${eF.mode==="quantita" ? "bg-white dark:bg-slate-900 text-amber-600 shadow-sm" : "text-slate-500"}`}>A quantità</button>
          </div>
          {eF.mode === "ore" ? (eF.id ? <>
            {/* EDIT legacy single-row */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subappaltatore (legacy)</Label>
                <Select value={eF.subappaltatore_id} onValueChange={(v) => setEF({ ...eF, subappaltatore_id: v })}>
                  <SelectTrigger data-testid="mane-ore-sub"><SelectValue placeholder="— (opzionale)" /></SelectTrigger>
                  <SelectContent>{subs.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tipo operaio</Label><Input value={eF.tipo_operaio} onChange={(e) => setEF({ ...eF, tipo_operaio: e.target.value })} data-testid="mane-ore-tipo" placeholder="Es. Muratore" /></div>
            </div>
            <div><Label>Operaio (nome) *</Label><Input required value={eF.operaio_nome} onChange={(e) => setEF({ ...eF, operaio_nome: e.target.value })} data-testid="riga-mane-nome" /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Data</Label><Input type="date" required value={eF.data} onChange={(e) => setEF({ ...eF, data: e.target.value })} /></div>
              <div><Label>N. operai</Label><Input type="number" min="1" value={eF.num_operai} onChange={(e) => setEF({ ...eF, num_operai: e.target.value })} data-testid="mane-ore-n" /></div>
              <div><Label>Ore *</Label><Input type="number" step="0.5" required value={eF.ore} onChange={(e) => setEF({ ...eF, ore: e.target.value })} data-testid="riga-mane-ore" /></div>
              <div><Label>€/h *</Label><Input type="number" step="0.01" required value={eF.costo_orario} onChange={(e) => setEF({ ...eF, costo_orario: e.target.value })} /></div>
            </div>
            {eF.ore && eF.costo_orario && <div className="text-xs">Totale: <b className="text-amber-600 font-mono">€ {fmt(Number(eF.ore)*Number(eF.costo_orario)*(parseInt(eF.num_operai)||1))}</b> = {eF.num_operai||1} × {eF.ore} × {eF.costo_orario}</div>}
            <div><Label>Note</Label><Input value={eF.note} onChange={(e) => setEF({ ...eF, note: e.target.value })} /></div>
          </> : <>
            {/* NUOVO record — multi-dipendente da Anagrafica Subappaltatori */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Subappaltatore *</Label>
                <Select value={anagSubId} onValueChange={setAnagSubId}>
                  <SelectTrigger data-testid="mane-anag-sub"><SelectValue placeholder="Seleziona subappaltatore" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— nessuno —</SelectItem>{anagSubs.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data *</Label><Input type="date" required value={eF.data} onChange={(e) => setEF({ ...eF, data: e.target.value })} data-testid="mane-multi-data" /></div>
            </div>
            <div className="border rounded-md p-3 bg-slate-50 dark:bg-slate-900/40">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Dipendenti impiegati</div>
                <Button type="button" size="sm" variant="outline" onClick={addLine} disabled={anagSubId === "none"} data-testid="mane-multi-add"><Plus className="h-3 w-3 mr-1" /> Aggiungi dipendente</Button>
              </div>
              {anagSubId === "none" ? (
                <p className="text-xs text-slate-500 text-center py-2">Seleziona prima un subappaltatore.</p>
              ) : anagDips.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-2">Nessun dipendente attivo per questo subappaltatore. Aggiungili da &quot;Anagrafica subappaltatori&quot;.</p>
              ) : (
                <div className="space-y-2">
                  {lines.map((l, i) => {
                    const rowTot = Number(l.ore || 0) * Number(l.costo_orario || 0);
                    return (
                      <div key={l._key || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`mane-multi-line-${i}`}>
                        <div className="col-span-4"><Label className="text-[10px]">Dipendente *</Label>
                          <Select value={l.dipendente_id} onValueChange={(v) => onPickLineDip(i, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`mane-multi-dip-${i}`}><SelectValue placeholder="Scegli…" /></SelectTrigger>
                            <SelectContent>{anagDips.filter(d => d.id === l.dipendente_id || !usedLineDipIds.has(d.id)).map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.cognome} {d.nome}{d.ruolo ? ` · ${d.ruolo}` : ""}</SelectItem>
                            ))}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3"><Label className="text-[10px]">Tipo operaio</Label>
                          <Input className="h-8 text-xs" value={l.tipo_operaio} onChange={(e) => updateLine(i, { tipo_operaio: e.target.value })} placeholder="Es. Muratore" data-testid={`mane-multi-tipo-${i}`} />
                        </div>
                        <div className="col-span-2"><Label className="text-[10px]">Ore *</Label>
                          <Input className="h-8 text-xs" type="number" step="0.5" min="0" value={l.ore} onChange={(e) => updateLine(i, { ore: e.target.value })} data-testid={`mane-multi-ore-${i}`} />
                        </div>
                        <div className="col-span-2"><Label className="text-[10px]">€/h *</Label>
                          <Input className="h-8 text-xs" type="number" step="0.01" min="0" value={l.costo_orario} onChange={(e) => updateLine(i, { costo_orario: e.target.value })} data-testid={`mane-multi-costo-${i}`} />
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                          <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)} disabled={lines.length === 1} data-testid={`mane-multi-del-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                        </div>
                        <div className="col-span-12 text-right text-[10px] font-mono text-slate-500 -mt-1">Totale riga: € {fmt(rowTot || 0)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-2 pt-2 border-t text-right text-sm font-semibold">Totale complessivo: <span className="text-amber-600 font-mono">€ {fmt(lines.reduce((s, l) => s + (Number(l.ore || 0) * Number(l.costo_orario || 0)), 0))}</span></div>
            </div>
            <div><Label>Note</Label><Input value={eF.note} onChange={(e) => setEF({ ...eF, note: e.target.value })} data-testid="mane-multi-note" /></div>
          </>) : <>
            <div className="text-xs bg-slate-50 dark:bg-slate-900 rounded p-2">Q.tà computo: <b className="font-mono">{fmt(riga.quantita)} {um}</b></div>
            <div><Label>Data *</Label><Input type="date" required value={eF.data} onChange={(e) => setEF({ ...eF, data: e.target.value })} data-testid="mane-data" /></div>
            <div><Label>Subappaltatore *</Label>
              <Select value={eF.subappaltatore_id} onValueChange={(v) => { setEF({ ...eF, subappaltatore_id: v }); autoFillPrezzo(v, "eff"); }}>
                <SelectTrigger data-testid="mane-sub"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{anagSubs.map(s => <SelectItem key={s.id} value={s.id}>{s.ragione_sociale || s.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Q.tà rilevata ({um}) *</Label><Input type="number" step="0.0001" required value={eF.quantita_rilevata} onChange={(e) => setEF({ ...eF, quantita_rilevata: e.target.value })} data-testid="mane-qta" /></div>
              <div><Label>Prezzo contrattuale €/UM</Label><Input type="number" step="0.0001" value={eF.prezzo_contrattuale} onChange={(e) => setEF({ ...eF, prezzo_contrattuale: e.target.value })} placeholder="auto se già presente" data-testid="mane-prezzo" /></div>
            </div>
            <div><Label>Note</Label><Input value={eF.note} onChange={(e) => setEF({ ...eF, note: e.target.value })} /></div>
            {eF.quantita_rilevata && eF.prezzo_contrattuale && <div className="text-xs">Importo: <b className="text-amber-600 font-mono">€ {fmt(Number(eF.quantita_rilevata)*Number(eF.prezzo_contrattuale))}</b></div>}
          </>}
          <DialogFooter><Button type="submit" data-testid="riga-mane-submit" className="bg-amber-500 text-slate-950 font-semibold">Registra</Button></DialogFooter>
        </form>
      </DialogContent></Dialog>

      <Dialog open={!!rilievoEdit} onOpenChange={(o) => !o && setRilievoEdit(null)}>
        <DialogContent><DialogHeader><DialogTitle>Modifica registrazione a quantità</DialogTitle><DialogDescription>Aggiorna il rilievo di lavorazione (fonte unica: Rilievi/SAL).</DialogDescription></DialogHeader>
          {rilievoEdit && <form onSubmit={saveRilievoEdit} className="space-y-3">
            <div><Label>Data *</Label><Input type="date" required value={rilievoEdit.data_rilievo} onChange={(e) => setRilievoEdit({ ...rilievoEdit, data_rilievo: e.target.value })} data-testid="rilievo-edit-data" /></div>
            <div><Label>Subappaltatore</Label>
              <Select value={rilievoEdit.subappaltatore_id} onValueChange={(v) => setRilievoEdit({ ...rilievoEdit, subappaltatore_id: v })}>
                <SelectTrigger data-testid="rilievo-edit-sub"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{subs.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Q.tà rilevata ({um}) *</Label><Input type="number" step="0.0001" required value={rilievoEdit.quantita_rilevata} onChange={(e) => setRilievoEdit({ ...rilievoEdit, quantita_rilevata: e.target.value })} data-testid="rilievo-edit-qta" /></div>
            <div><Label>Note</Label><Input value={rilievoEdit.note} onChange={(e) => setRilievoEdit({ ...rilievoEdit, note: e.target.value })} data-testid="rilievo-edit-note" /></div>
            <DialogFooter><Button type="submit" data-testid="rilievo-edit-submit" className="bg-amber-500 text-slate-950 font-semibold">Salva</Button></DialogFooter>
          </form>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotePerRiga({ riga, canEdit }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [testo, setTesto] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editTesto, setEditTesto] = useState("");
  const load = async () => { const { data } = await api.get(`/note?riga_computo_id=${riga.id}`); setNotes(data); };
  useEffect(() => { load(); }, [riga.id]);
  const add = async () => {
    if (!testo.trim()) return;
    try {
      await api.post("/note", { cantiere_id: riga.cantiere_id, capitolo_id: riga.capitolo_id, riga_computo_id: riga.id, testo });
      setTesto(""); toast.success("Nota aggiunta"); load();
    } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const startEdit = (n) => { setEditingId(n.id); setEditTesto(n.testo); };
  const cancelEdit = () => { setEditingId(null); setEditTesto(""); };
  const saveEdit = async (id) => {
    if (!editTesto.trim()) return;
    try {
      await api.patch(`/note/${id}`, { testo: editTesto });
      toast.success("Nota aggiornata"); cancelEdit(); load();
    } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; try { await api.delete(`/note/${id}`); load(); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const wasEdited = (n) => {
    if (!n.updated_at || !n.created_at) return false;
    // consider modified if updated_at differs from created_at by more than 1 second
    return Math.abs(new Date(n.updated_at).getTime() - new Date(n.created_at).getTime()) > 1000;
  };
  return (
    <Card><CardContent className="p-6">
      <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-3">Note contestualizzate su questa voce</div>
      {canEdit && <div className="flex gap-2 mb-4">
        <textarea placeholder="Scrivi una nota per questa voce…" value={testo} onChange={(e) => setTesto(e.target.value)} rows={2} className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-800 rounded-md bg-white dark:bg-slate-900 text-sm resize-none" data-testid="riga-nota-input" />
        <Button size="sm" onClick={add} disabled={!testo.trim()} data-testid="riga-nota-submit" className="bg-amber-500 text-slate-950 font-semibold self-end">Aggiungi</Button>
      </div>}
      {notes.length === 0 ? <p className="text-center text-slate-500 py-4 text-sm">Nessuna nota su questa voce</p> : (
        <div className="space-y-2">{notes.map(n => {
          const canModify = user?.role === "ADMIN" || user?.full_name === n.author_name;
          const isEditing = editingId === n.id;
          return (
            <div key={n.id} className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/20 rounded-r-md p-3" data-testid={`riga-nota-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {isEditing ? (
                    <textarea value={editTesto} onChange={(e) => setEditTesto(e.target.value)} rows={2} className="w-full px-2 py-1 border border-amber-300 rounded-md bg-white dark:bg-slate-900 text-sm resize-none" data-testid={`riga-nota-edit-input-${n.id}`} />
                  ) : (
                    <div className="text-sm whitespace-pre-wrap">{n.testo}</div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span><b>{n.author_name}</b> · {new Date(n.created_at).toLocaleString("it-IT")}</span>
                    {wasEdited(n) && <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 border-amber-500 text-amber-700 dark:text-amber-400" data-testid={`riga-nota-modificata-${n.id}`}>Modificata · {new Date(n.updated_at).toLocaleString("it-IT")}</Badge>}
                  </div>
                </div>
                {canModify && (
                  <div className="flex gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => saveEdit(n.id)} data-testid={`riga-nota-save-${n.id}`}><Check className="h-3.5 w-3.5 text-emerald-600" /></Button>
                        <Button size="icon" variant="ghost" onClick={cancelEdit} data-testid={`riga-nota-cancel-${n.id}`}><X className="h-3.5 w-3.5 text-slate-500" /></Button>
                      </>
                    ) : (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => startEdit(n)} data-testid={`riga-nota-edit-${n.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => del(n.id)} data-testid={`riga-nota-del-${n.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}</div>
      )}
    </CardContent></Card>
  );
}


function FotoPerRiga({ riga, foto, onChange, canEdit }) {
  const upload = async (e) => {
    const files = e.target.files; if (!files?.length) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("tipo", "foto"); fd.append("link_type", "riga_computo");
      fd.append("cantiere_id", riga.cantiere_id); fd.append("riga_computo_id", riga.id);
      try { await api.post("/allegati/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
    }
    toast.success("Foto caricate"); onChange(); e.target.value = "";
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/allegati/${id}`); onChange(); };
  return (
    <Card><CardContent className="p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Foto della lavorazione</div>
        {canEdit && <label className="cursor-pointer"><input type="file" multiple accept="image/*" capture="environment" className="hidden" onChange={upload} data-testid="riga-foto-input" /><span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold text-sm"><Camera className="h-3.5 w-3.5" /> + Foto</span></label>}
      </div>
      {foto.length === 0 ? <p className="text-center text-slate-500 py-6">Nessuna foto. Dallo smartphone puoi scattare direttamente con la fotocamera.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">{foto.map(a => (
          <div key={a.id} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-800 cursor-zoom-in" onClick={() => openAllegato(a.id, a.filename)}>
            <AllegatoImg id={a.id} className="w-full h-full object-cover" alt={a.filename || ""} />
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">{a.created_at?.slice(0,10)}</div>
            {canEdit && <button onClick={(e) => { e.stopPropagation(); del(a.id); }} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-rose-600 text-white rounded p-1"><Trash2 className="h-3 w-3" /></button>}
          </div>
        ))}</div>
      )}
    </CardContent></Card>
  );
}

function RilieviPerRiga({ riga, canEdit }) {
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDlg, setOpenDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { subappaltatore_id: "", data_rilievo: new Date().toISOString().slice(0, 10), quantita_rilevata: "", note: "" };
  const [form, setForm] = useState(emptyForm);
  const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rl, ps] = await Promise.all([
        api.get(`/righe/${riga.id}/rilievi`),
        api.get(`/anagrafiche/subappaltatori`),
      ]);
      setSummary(rl.data);
      setSubs(ps.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento rilievi");
    }
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, [riga.id]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpenDlg(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      // Preselect via nuova anagrafica; fallback su legacy id
      subappaltatore_id: r.subappaltatore_anag_id || r.subappaltatore_id || "",
      data_rilievo: r.data_rilievo,
      quantita_rilevata: String(r.quantita_rilevata),
      note: r.note || "",
    });
    setOpenDlg(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const body = {
      subappaltatore_id: form.subappaltatore_id || null,
      data_rilievo: form.data_rilievo,
      quantita_rilevata: Number(form.quantita_rilevata),
      note: form.note || null,
    };
    try {
      if (editing) {
        const { data } = await api.patch(`/rilievi/${editing.id}`, body);
        setSummary(data);
        toast.success("Rilievo aggiornato");
      } else {
        const { data } = await api.post(`/righe/${riga.id}/rilievi`, body);
        setSummary(data);
        toast.success("Rilievo registrato");
      }
      setOpenDlg(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };
  const del = async (id) => {
    if (!confirm("Eliminare questo rilievo?")) return;
    try {
      const { data } = await api.delete(`/rilievi/${id}`);
      setSummary(data);
      toast.success("Rilievo eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  if (loading || !summary) return <Card><CardContent className="p-6"><p className="text-slate-500 text-sm">Caricamento rilievi…</p></CardContent></Card>;

  const pct = summary.percentuale || 0;
  const sforamento = summary.quantita_computo > 0 && summary.cumulativo > summary.quantita_computo;
  const eccedenza = summary.cumulativo - summary.quantita_computo;
  const pctColor = (() => {
    if (sforamento) return "rose";
    if (pct >= 100) return "emerald";
    if (pct >= 75) return "amber";
    if (pct >= 25) return "sky";
    return "slate";
  })();
  const pctBg = { rose: "bg-rose-500", emerald: "bg-emerald-500", amber: "bg-amber-500", sky: "bg-sky-500", slate: "bg-slate-400" }[pctColor];

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Registro quantità lavorazioni rilevate</div>
            {sforamento && (
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-rose-600 text-white text-xs font-bold uppercase tracking-widest animate-pulse" data-testid="rilievi-sforamento-badge">
                ⚠ SFORAMENTO +{fmt(eccedenza)} {summary.unita_misura || ""} · {fmt(pct)}%
              </div>
            )}
          </div>
          {canEdit && <Button size="sm" onClick={openNew} data-testid="riga-add-rilievo-btn" className="bg-amber-500 text-slate-950 font-semibold"><Plus className="h-3 w-3 mr-1" /> Nuovo rilievo</Button>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <StatBox label={`Q.tà computo (${summary.unita_misura || "—"})`} v={summary.quantita_computo} />
          <StatBox label="Cumulativo" v={summary.cumulativo} highlight={sforamento ? "rose" : undefined} />
          <StatBox label={sforamento ? "Eccedenza" : "Residuo"} v={sforamento ? eccedenza : summary.residuo} highlight={sforamento ? "rose" : summary.residuo === 0 ? "emerald" : undefined} />
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Avanzamento reale</div>
            <div className={`text-xl font-extrabold mt-1 font-mono ${sforamento ? "text-rose-600" : ""}`} data-testid="rilievi-pct">{fmt(pct)}%</div>
            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full mt-2 overflow-hidden relative">
              <div className={`h-full ${pctBg} transition-all`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
              {sforamento && <div className="absolute inset-y-0 right-0 h-full bg-rose-700 opacity-70" style={{ width: `${Math.min(50, Math.max(0, pct - 100) / 2)}%` }} title={`+${fmt(pct - 100)}% oltre`} />}
            </div>
          </div>
          <StatBox label="N. rilievi" v={summary.rilievi.length} />
        </div>

        {summary.totali_per_subappaltatore.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Totali per subappaltatore</div>
            <div className="flex flex-wrap gap-2">
              {summary.totali_per_subappaltatore.map((s) => (
                <div key={s.subappaltatore_id || "none"} className="border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 bg-slate-50 dark:bg-slate-900" data-testid={`rilievi-sub-total-${s.subappaltatore_id || "none"}`}>
                  <div className="text-xs font-semibold">{s.subappaltatore_nome}</div>
                  <div className="text-xs text-slate-500 font-mono">{fmt(s.totale)} {summary.unita_misura || ""} · {s.count} rilievi</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Subappaltatore</TableHead>
              <TableHead className="text-right">Q.tà rilevata</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Autore</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.rilievi.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-500">Nessun rilievo ancora registrato</TableCell></TableRow>
            ) : summary.rilievi.map((r) => (
              <TableRow key={r.id} data-testid={`rilievo-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.data_rilievo}</TableCell>
                <TableCell className="text-sm">{r.subappaltatore_nome || <span className="italic text-slate-400">— non assegnato —</span>}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{fmt(r.quantita_rilevata)} <span className="text-xs text-slate-500">{summary.unita_misura || ""}</span></TableCell>
                <TableCell className="text-xs text-slate-600 dark:text-slate-400 max-w-xs truncate">{r.note || "—"}</TableCell>
                <TableCell className="text-xs">{r.author_name}</TableCell>
                <TableCell className="text-right">
                  {canEdit && <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`rilievo-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`rilievo-del-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica rilievo" : "Nuovo rilievo"}</DialogTitle>
            <DialogDescription>Q.tà computo <b className="font-mono">{fmt(summary.quantita_computo)} {summary.unita_misura || ""}</b> — presa dalla riga (non modificabile).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data rilievo *</Label>
                <Input type="date" required value={form.data_rilievo} onChange={(e) => setForm({ ...form, data_rilievo: e.target.value })} data-testid="rilievo-data" />
              </div>
              <div>
                <Label>Quantità rilevata ({summary.unita_misura || "—"}) *</Label>
                <Input type="number" step="0.0001" min="0.0001" required value={form.quantita_rilevata} onChange={(e) => setForm({ ...form, quantita_rilevata: e.target.value })} data-testid="rilievo-qta" />
              </div>
            </div>
            <div>
              <Label>Subappaltatore</Label>
              <Select value={form.subappaltatore_id || "__none__"} onValueChange={(v) => setForm({ ...form, subappaltatore_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="rilievo-sub-select"><SelectValue placeholder="Seleziona subappaltatore" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— nessuno —</SelectItem>
                  {subs.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`rilievo-sub-opt-${s.id}`}>
                      {s.nome}{s.ragione_sociale ? ` · ${s.ragione_sociale}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {subs.length === 0 && <p className="text-[10px] text-slate-500 mt-1">Nessun subappaltatore in anagrafica. Aggiungine uno da <b>Anagrafica → Subappaltatori</b>.</p>}
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="rilievo-note" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-amber-500 text-slate-950 font-semibold" data-testid="rilievo-submit">{editing ? "Salva" : "Registra"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// FASE C.3 — Blocco AUTO INVILUPPO visibile dentro RigaDetail (MDO/Materiali)
// READ-ONLY. Nessuna scrittura DB.
// ============================================================================
function AutoInviluppoBlock({ inv, section }) {
  if (!inv) return null;
  const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d }));
  const fmt4 = (n) => fmt(n, 4);
  const isApplied = inv.state === "applied";
  const isStale = inv.state === "stale";
  const um = inv.unita_misura || "UM";
  const modeLabel = {
    specific_rule: "Regola categoria",
    missing_mdo_fallback: "Fallback MDO mancante (20% MDO / 50% Mat)",
    out_of_range_fallback: "Fuori intervallo IncMDO (fallback 30% margine)",
    demolition_30: "Demolizioni / Scavi (target 30% margine)",
    category_fallback_30: "Categoria non configurata (fallback 30% margine)",
    needs_review: "Da verificare (MDO INVILUPPO > prezzo)",
    non_economic_row: "Riga non economica",
  }[inv.mode] || inv.mode;

  const borderClass = isApplied
    ? "border-sky-300 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-950/30"
    : isStale
      ? "border-orange-300 dark:border-orange-800 bg-orange-50/60 dark:bg-orange-950/30"
      : "border-violet-300 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30";

  const badge = isApplied ? (
    <span className="text-[10px] font-semibold uppercase tracking-widest text-sky-700 dark:text-sky-300 border border-sky-500/50 rounded px-2 py-[2px]" data-testid={`inv-auto-badge-applied-${section}`}>
      AUTO INVILUPPO APPLICATO
    </span>
  ) : isStale ? (
    <span className="text-[10px] font-semibold uppercase tracking-widest text-orange-700 dark:text-orange-300 border border-orange-500/50 rounded px-2 py-[2px]" data-testid={`inv-auto-badge-stale-${section}`}>
      AUTO INVILUPPO — DA RICALCOLARE
    </span>
  ) : (
    <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-700 dark:text-violet-300 border border-violet-500/50 rounded px-2 py-[2px]" data-testid={`inv-auto-badge-preview-${section}`}>
      ANTEPRIMA AUTO INVILUPPO — NON ANCORA APPLICATA
    </span>
  );

  const notApplied = !isApplied && !isStale;
  const notice = notApplied ? (
    <div className="text-[10px] text-violet-700 dark:text-violet-300 italic pt-1" data-testid={`inv-auto-notice-preview-${section}`}>
      Questi valori sono calcolati con le regole INVILUPPO ma non sono ancora stati salvati.
      Premi <b>COMPILA AUTO MAT + MDO</b> nella pagina Computo per salvarli.
    </div>
  ) : isStale ? (
    <div className="text-[10px] text-orange-700 dark:text-orange-300 italic pt-1" data-testid={`inv-auto-notice-stale-${section}`}>
      Un input (prezzo/quantità/incidenza/categoria) è cambiato dopo l'ultimo Apply.
      Premi <b>Ricalcola Auto</b> nella pagina Computo per aggiornare lo snapshot.
    </div>
  ) : (
    <div className="text-[10px] text-sky-700 dark:text-sky-300 italic pt-1" data-testid={`inv-auto-notice-applied-${section}`}>
      Preset <b>{inv.preset} v{inv.preset_version}</b>{inv.applied_at ? ` · Ultimo Apply: ${new Date(inv.applied_at).toLocaleString("it-IT")}` : ""}
    </div>
  );

  if (section === "mdo") {
    return (
      <div className={`border rounded-lg p-3 space-y-2 mt-3 ${borderClass}`} data-testid="inv-auto-block-mdo">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold flex items-center gap-2">
            AUTO INVILUPPO — Manodopera
          </div>
          {badge}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><div className="text-[10px] uppercase text-slate-500">Incidenza MDO XPWE</div><div className="font-mono font-semibold" data-testid="inv-auto-mdo-inc">{fmt(inv.inc_mdo_percent)}%{inv.mdo_cap_applied ? (
            <span
              className="ml-1 inline-flex items-center rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
              title={`Incidenza XPWE ${inv.inc_mdo_percent}% oltre il tetto della categoria — Auto INVILUPPO calcolato con ${fmt(inv.inc_mdo_effective_percent)}% (tetto categoria)`}
              data-testid="inv-auto-mdo-cap-badge"
            >
              cap {fmt(inv.inc_mdo_effective_percent)}%
            </span>
          ) : null}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500">MDO originale XPWE</div><div className="font-mono font-semibold" data-testid="inv-auto-mdo-orig">€ {fmt4(inv.mdo_originale_unit)}/{um}</div></div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Regola categoria</div>
            <div className="font-mono font-semibold" data-testid="inv-auto-mdo-cat">{inv.categoria_codice || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Variazione MDO INVILUPPO</div>
            <div className="font-mono font-semibold" data-testid="inv-auto-mdo-var">
              {inv.mdo_variation_percent == null ? "—" : `${Number(inv.mdo_variation_percent) > 0 ? "+" : ""}${inv.mdo_variation_percent}%`}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs items-end pt-2 border-t border-slate-200 dark:border-slate-700">
          <div>
            <div className="text-[10px] uppercase text-slate-500">MDO INVILUPPO</div>
            <div className="font-mono font-bold text-lg text-sky-700 dark:text-sky-400" data-testid="inv-auto-mdo-inv">€ {fmt4(inv.mdo_inviluppo_unit)}/{um}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Quantità × MDO INVILUPPO</div>
            <div className="font-mono font-bold text-lg text-sky-700 dark:text-sky-400" data-testid="inv-auto-mdo-total">€ {fmt(inv.mdo_total)}</div>
            <div className="text-[10px] text-slate-500">Q.tà: <span className="font-mono">{fmt(inv.quantita)} {um}</span></div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Modalità</div>
            <div className="text-[11px] font-medium" data-testid="inv-auto-mdo-mode">{modeLabel}</div>
            {inv.component_split === "not_detailed" && <div className="text-[10px] text-slate-500 italic">Composizione non dettagliata</div>}
          </div>
        </div>
        <DistributionCard dist={inv.mdo_distribution} section="mdo" um={um} />
        {notice}
      </div>
    );
  }

  return (
    <div className={`border rounded-lg p-3 space-y-2 mt-3 ${borderClass}`} data-testid="inv-auto-block-mate">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold flex items-center gap-2">
          AUTO INVILUPPO — Materiali
        </div>
        {badge}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><div className="text-[10px] uppercase text-slate-500">Prezzo voce</div><div className="font-mono font-semibold" data-testid="inv-auto-mate-p">€ {fmt4(inv.prezzo_unitario)}/{um}</div></div>
        <div><div className="text-[10px] uppercase text-slate-500">MDO INVILUPPO (già calcolata)</div><div className="font-mono font-semibold" data-testid="inv-auto-mate-mdo">€ {fmt4(inv.mdo_inviluppo_unit)}/{um}</div></div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">Base Materiale dopo MDO</div>
          <div className="font-mono font-semibold" data-testid="inv-auto-mate-base">€ {fmt4(inv.base_materiale_unit)}/{um}</div>
          <div className="text-[9px] text-slate-500 italic">= Prezzo − MDO INVILUPPO</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">Variazione Materiali</div>
          <div className="font-mono font-semibold" data-testid="inv-auto-mate-var">
            {inv.materiali_variation_percent == null ? "—" : `${Number(inv.materiali_variation_percent) > 0 ? "+" : ""}${inv.materiali_variation_percent}%`}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs items-end pt-2 border-t border-slate-200 dark:border-slate-700">
        <div>
          <div className="text-[10px] uppercase text-slate-500">Materiali INVILUPPO</div>
          <div className="font-mono font-bold text-lg text-sky-700 dark:text-sky-400" data-testid="inv-auto-mate-inv">€ {fmt4(inv.materiali_inviluppo_unit)}/{um}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">Quantità × Materiali INVILUPPO</div>
          <div className="font-mono font-bold text-lg text-sky-700 dark:text-sky-400" data-testid="inv-auto-mate-total">€ {fmt(inv.materiali_total)}</div>
          <div className="text-[10px] text-slate-500">Q.tà: <span className="font-mono">{fmt(inv.quantita)} {um}</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">Modalità</div>
          <div className="text-[11px] font-medium" data-testid="inv-auto-mate-mode">{modeLabel}</div>
          {inv.component_split === "not_detailed" && <div className="text-[10px] text-slate-500 italic">Composizione non dettagliata</div>}
        </div>
      </div>
      <DistributionCard dist={inv.mate_distribution} section="mate" um={um} />
      {notice}
    </div>
  );
}

// ============================================================================
// FASE C.5.1 — Tracciabilità del costo di categoria distribuito
// Mostrata solo quando dist.mode === "manual_cost".
// ============================================================================
function DistributionCard({ dist, section, um }) {
  if (!dist || dist.mode !== "manual_cost") return null;
  const fmtEur = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const fmtPct = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const label = section === "mdo" ? "MDO" : "Materiali";
  const varLabel = section === "mdo"
    ? "Percentuale utilizzata per il peso (MDO)"
    : "Percentuale utilizzata per il peso (Materiali)";
  const effVar = dist.effective_var_pct;
  const varSource = dist.var_source === "override" ? "Personalizzato" : "Preset v2";
  const varDisplay = effVar == null
    ? "—"
    : `${Number(effVar) > 0 ? "+" : ""}${effVar}%`;
  return (
    <div
      className="mt-2 border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 rounded-md p-2 space-y-1"
      data-testid={`inv-auto-${section}-dist-card`}
    >
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-800 dark:text-amber-300">
          Origine {label}
        </div>
        <span
          className="text-[10px] font-semibold uppercase text-amber-900 dark:text-amber-200 border border-amber-500/60 rounded px-2 py-[1px]"
          data-testid={`inv-auto-${section}-dist-origin`}
        >
          Costo {label} categoria distribuito
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <div>
          <div className="text-[9px] uppercase text-slate-500">Costo {label} categoria</div>
          <div className="font-mono font-semibold" data-testid={`inv-auto-${section}-dist-cost`}>
            € {fmtEur(dist.cost)}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-500">Peso della voce</div>
          <div className="font-mono font-semibold" data-testid={`inv-auto-${section}-dist-weight`}>
            {fmtPct(dist.weight_pct)}%
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-500">{label} assegnati alla voce</div>
          <div className="font-mono font-semibold text-amber-900 dark:text-amber-200" data-testid={`inv-auto-${section}-dist-assigned`}>
            € {fmtEur(dist.assigned)}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-500">{varLabel}</div>
          <div className="font-mono font-semibold" data-testid={`inv-auto-${section}-dist-varpct`}>
            {varDisplay}
          </div>
          <div className="text-[9px] italic text-slate-500" data-testid={`inv-auto-${section}-dist-varsource`}>
            {varSource}
          </div>
        </div>
      </div>
    </div>
  );
}
